import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf.mjs";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";
import * as fabric from "fabric";
import type { FabricObject, ModifiedEvent, TPointerEventInfo } from "fabric";

/**
 * Fabric 7 : `originX`/`originY` par défaut = `center` (breaking vs v5 `left`/`top`).
 * Notre contrat éditeur ↔ PDF suppose le coin haut-gauche ; on rétablit les défauts v5
 * sur la classe de base qui porte `fabricObjectDefaultValues`.
 */
(() => {
  let C: typeof fabric.FabricObject | null = fabric.FabricObject;
  for (let i = 0; i < 6 && C; i += 1) {
    const od = (C as typeof fabric.FabricObject & { ownDefaults?: { originX?: string; originY?: string } }).ownDefaults;
    if (od && od.originX === "center") {
      od.originX = "left";
      od.originY = "top";
      break;
    }
    C = Object.getPrototypeOf(C) as typeof fabric.FabricObject | null;
  }
})();

import U2000BasicDialog from "../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000YesNoDialog from "../../../u2000Core/components/dialog/U2000YesNoDialog";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ComboBox from "../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import type { U2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../u2000Core/reducer/formReducer/U2000FormState";
import {
  type U3140DocumentLogicalUnit,
  type U3140DocumentPageSegment,
  type U3140DocumentPdfAnnotationsLotPayload,
  type U3140DocumentPdfAnnotationsPayload,
  type U3140PdfAnnotationToolbarPreset,
  type U3140PdfAnnotationToolbarPresetStyles,
  type U3140PdfAnnotationShape,
  type U3140PdfAnnotationTextAlign,
  type U3140PdfAnnotationTextBoxVerticalAlign,
  u3140MergeToolbarPresetStyles,
  u3140MaterializeRectPresetStylesForSave,
  u3140NewPresetRectStyleSeed,
  u3140RectStyleForPlacement,
  u3140ResolveRectCanvasSize,
} from "../../U3140PdfAnnotationsService";
import { useU3140UsePdfAnnotationHandlers } from "./U3140UsePdfAnnotationHandlers";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { FaAlignCenter, FaAlignLeft, FaAlignRight, FaChevronLeft, FaChevronRight, FaRedo, FaTrash, FaUndo } from "react-icons/fa";
import { MdVerticalAlignBottom, MdVerticalAlignCenter, MdVerticalAlignTop } from "react-icons/md";
import {
  formatConcordanceEntryForDisplay,
  type U3141ConcordanceParentContext,
} from "../../../u3141LandRegister/components/form/u3141ChildLotChain/U3141ConcordanceUtils";
import { U3140_PDF_ANNOTATION_FILL_NONE } from "../../U3140PdfAnnotationsPalette";
import { U3140PdfAnnotationColorMenuButton } from "./U3140PdfAnnotationColorMenuButton";
import { U3140PdfAnnotationTextColorMenuButton } from "./U3140PdfAnnotationTextColorMenuButton";
import {
  U3140PdfAnnotationShapesMenuButton,
  type U3140PdfAnnotationInsertToolId,
} from "./U3140PdfAnnotationShapesMenuButton";
import { useU3140UsePdfAnnotationToolbarPresetHandlers } from "./U3140UsePdfAnnotationToolbarPresetHandlers";
import { U3140PdfAnnotationToolbarPresetStyleForm } from "./U3140PdfAnnotationToolbarPresetStyleForm";
import { U3140PdfAnnotationToolbarPresetSidebar } from "./U3140PdfAnnotationToolbarPresetSidebar";
import { useU3140PdfDocumentBuffer } from "./useU3140PdfDocumentBuffer";

GlobalWorkerOptions.workerSrc = pdfjsWorker;

/** Historique d’annulation : regroupe les `mergeShapes` rapprochés (ex. déplacement) en une entrée. */
const U3142_PDF_ANNOTATION_HISTORY_DEBOUNCE_MS = 400;
const U3142_PDF_ANNOTATION_HISTORY_MAX = 60;

/** Snapshot des champs du dialogue préréglage (Annuler / après enregistrement ou suppression). */
type U3142PresetDialogBaseline = {
  presetDialogMode: "edit" | "create";
  presetEditId: string | null;
  createSourcePresetId: string | null;
  presetLabelDraft: string;
  presetHintDraft: string;
  presetStylesDraft: U3140PdfAnnotationToolbarPresetStyles;
  presetRenameOpen: boolean;
};

/** Zoom min / max (PDF.js) une fois la largeur utile connue. */
const PDF_SCALE_MIN = 0.05;
const PDF_SCALE_MAX = 4;
/** Marge horizontale retirée : le PDF doit occuper toute la largeur de la zone de défilement. */
const PDF_AREA_MARGIN_PX = 0;
/** Ignorer les callbacks ResizeObserver si seule la hauteur change (ex. ligne d’aide qui apparaît) : évite un re-render PDF + Fabric complet = flicker. */
const PDF_SCROLL_AREA_WIDTH_RERENDER_EPSILON_PX = 2;

/** Largeur par défaut du bloc texte (`Textbox`), fraction de la largeur du viewport PDF (0–1). */
const U3142_PDF_TEXT_BOX_WIDTH_DEFAULT_NORM = 0.38;

/** Largeur minimale en fraction du viewport (évite un bloc trop étroit). */
const U3142_PDF_TEXT_BOX_WIDTH_MIN_NORM = 0.04;
/** Hauteur minimale du cadre texte en fraction de la hauteur viewport (évite un cadre nul). */
const U3142_PDF_TEXT_BOX_HEIGHT_MIN_NORM = 0.015;

function u3142ClampTextBoxWidthNorm(n: number): number {
  return Math.min(1, Math.max(U3142_PDF_TEXT_BOX_WIDTH_MIN_NORM, n));
}

function u3142ClampTextBoxHeightNorm(n: number): number {
  return Math.min(1, Math.max(U3142_PDF_TEXT_BOX_HEIGHT_MIN_NORM, n));
}

/** Champs privés sur `fabric.Textbox` (hauteur min + alignement vertical dans le cadre). */
type U3142PdfTextboxAug = fabric.Textbox & {
  _u3142TextBoxMinHeightPx?: number;
  _u3142TextBoxVerticalAlign?: U3140PdfAnnotationTextBoxVerticalAlign;
};

type U3142AnnotationFabricMetadata = {
  annotationId?: string;
  createdBy?: string;
  createdByUser?: string;
  createdAt?: string;
};

type U3142FabricObjectWithMetadata = FabricObject & {
  _u3142Id?: string;
} & U3142AnnotationFabricMetadata;

function u3142ExtractAnnotationMetadataFromShape(shape: U3140PdfAnnotationShape): U3142AnnotationFabricMetadata {
  return {
    annotationId: shape.annotationId,
    createdBy: shape.createdBy,
    createdByUser: shape.createdByUser,
    createdAt: shape.createdAt,
  };
}

function u3142AttachAnnotationMetadataOnFabricObject(
  obj: FabricObject,
  metadata: U3142AnnotationFabricMetadata
): void {
  const target = obj as U3142FabricObjectWithMetadata;
  if (metadata.annotationId) target.annotationId = metadata.annotationId;
  if (metadata.createdBy) target.createdBy = metadata.createdBy;
  if (metadata.createdByUser) target.createdByUser = metadata.createdByUser;
  if (metadata.createdAt) target.createdAt = metadata.createdAt;
}

function u3142ReadAnnotationMetadataFromFabricObject(obj: FabricObject): U3142AnnotationFabricMetadata {
  const source = obj as U3142FabricObjectWithMetadata;
  return {
    annotationId: source.annotationId,
    createdBy: source.createdBy,
    createdByUser: source.createdByUser,
    createdAt: source.createdAt,
  };
}

function u3142ApplyAnnotationMetadataToShape(shape: U3140PdfAnnotationShape, obj: FabricObject): U3140PdfAnnotationShape {
  const metadata = u3142ReadAnnotationMetadataFromFabricObject(obj);
  return {
    ...shape,
    ...(metadata.annotationId ? { annotationId: metadata.annotationId } : {}),
    ...(metadata.createdBy ? { createdBy: metadata.createdBy } : {}),
    ...(metadata.createdByUser ? { createdByUser: metadata.createdByUser } : {}),
    ...(metadata.createdAt ? { createdAt: metadata.createdAt } : {}),
  };
}

function u3142NormalizeTextBoxVerticalAlign(raw: unknown): U3140PdfAnnotationTextBoxVerticalAlign {
  if (raw === "middle" || raw === "bottom") {
    return raw;
  }
  return "top";
}

let u3142TextboxInitDimensionsPatched = false;
let u3142TextBaselinePatched = false;
let u3142CanvasTextBaselineSetterPatched = false;

/** `fabric.Textbox.initDimensions` remet `height` au seul contenu ; on impose une hauteur min si définie. */
function u3142EnsureTextboxInitDimensionsPatch(): void {
  if (u3142TextboxInitDimensionsPatched) {
    return;
  }
  u3142TextboxInitDimensionsPatched = true;
  if (!fabric.Textbox?.prototype?.initDimensions) {
    return;
  }
  const proto = fabric.Textbox.prototype as fabric.Textbox & { initDimensions: () => void };
  const orig = proto.initDimensions;
  proto.initDimensions = function (this: fabric.Textbox) {
    orig.call(this);
    const ext = this as U3142PdfTextboxAug;
    const minH = ext._u3142TextBoxMinHeightPx;
    if (typeof minH === "number" && minH > 0 && (this.height ?? 0) < minH) {
      const setter = (this as fabric.Textbox & { _set?: (k: string, v: unknown) => void })._set;
      if (typeof setter === "function") {
        setter.call(this, "height", minH);
      } else {
        this.set("height", minH);
      }
    }
  };
}

/**
 * Certains snapshots Fabric peuvent porter `textBaseline: "alphabetical"` (valeur invalide DOM).
 * On la normalise vers `alphabetic` avant mesure/rendu pour supprimer le warning navigateur.
 */
function u3142EnsureTextBaselinePatch(): void {
  if (u3142TextBaselinePatched) {
    return;
  }
  u3142TextBaselinePatched = true;
  type U3142TextProto = Omit<fabric.FabricText, "_setTextStyles"> & {
    _setTextStyles?: (ctx: CanvasRenderingContext2D, styleDecl: Record<string, unknown>, forMeasuring?: boolean) => void;
  };
  if (!fabric.FabricText?.prototype) {
    return;
  }
  const proto = fabric.FabricText.prototype as U3142TextProto;
  const orig = proto._setTextStyles;
  if (typeof orig !== "function") {
    return;
  }
  proto._setTextStyles = function (this: fabric.FabricText, ctx: CanvasRenderingContext2D, styleDecl: Record<string, unknown>, forMeasuring?: boolean) {
    if (styleDecl?.textBaseline === "alphabetical") {
      styleDecl.textBaseline = "alphabetic";
    }
    const self = this as fabric.FabricText & { textBaseline?: string };
    if (self.textBaseline === "alphabetical") {
      self.textBaseline = "alphabetic";
    }
    orig.call(this as unknown as U3142TextProto, ctx, styleDecl, forMeasuring);
  };
}

/**
 * Garde-fou global : certains chemins Fabric tentent encore `textBaseline="alphabetical"`.
 * On intercepte l’affectation canvas pour normaliser avant validation navigateur.
 */
function u3142EnsureCanvasTextBaselineSetterPatch(): void {
  if (u3142CanvasTextBaselineSetterPatched) {
    return;
  }
  const proto = CanvasRenderingContext2D?.prototype as CanvasRenderingContext2D | undefined;
  if (!proto) {
    return;
  }
  const desc = Object.getOwnPropertyDescriptor(proto, "textBaseline");
  if (!desc?.set || !desc.get) {
    return;
  }
  u3142CanvasTextBaselineSetterPatched = true;
  const nativeSet = desc.set;
  const nativeGet = desc.get;
  Object.defineProperty(proto, "textBaseline", {
    configurable: true,
    enumerable: desc.enumerable ?? false,
    get: function (this: CanvasRenderingContext2D) {
      return nativeGet.call(this);
    },
    set: function (this: CanvasRenderingContext2D, value: string) {
      nativeSet.call(this, value === "alphabetical" ? "alphabetic" : value);
    },
  });
}

/** Décale verticalement le contenu dans le cadre (deltaY Fabric sur chaque glyphe). */
function u3142ApplyTextBoxVerticalAlignDeltaY(t: fabric.Textbox): void {
  const ext = t as U3142PdfTextboxAug;
  const valign = u3142NormalizeTextBoxVerticalAlign(ext._u3142TextBoxVerticalAlign);
  const text = t.text ?? "";
  const len = text.length;
  const contentH = t.calcTextHeight();
  const minHPx = typeof ext._u3142TextBoxMinHeightPx === "number" && ext._u3142TextBoxMinHeightPx > 0 ? ext._u3142TextBoxMinHeightPx : 0;
  const boxH = Math.max(t.height ?? 0, contentH, minHPx);
  const slack = Math.max(0, boxH - contentH);
  let offset = 0;
  if (valign === "middle") {
    offset = slack / 2;
  } else if (valign === "bottom") {
    offset = slack;
  }
  if (len === 0) {
    return;
  }
  t.setSelectionStyles(offset === 0 ? { deltaY: 0 } : { deltaY: offset }, 0, len);
  t.set({ dirty: true });
}

function u3142RefreshPdfTextboxLayout(t: fabric.Textbox): void {
  const ext = t as U3142PdfTextboxAug;
  const minH = ext._u3142TextBoxMinHeightPx;
  if (typeof minH === "number" && minH > 0) {
    const ch = t.calcTextHeight();
    const next = Math.max(minH, ch);
    if ((t.height ?? 0) < next - 0.5) {
      t.set({ height: next });
    }
  }
  u3142ApplyTextBoxVerticalAlignDeltaY(t);
  t.setCoords();
}

u3142EnsureTextboxInitDimensionsPatch();
u3142EnsureTextBaselinePatch();
u3142EnsureCanvasTextBaselineSetterPatch();

function isFabricTextBackgroundTransparent(raw: string | undefined | null): boolean {
  if (!raw || raw === "transparent") return true;
  if (raw === "rgba(255,255,255,0)") return true;
  if (/^rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/i.test(raw)) return true;
  return false;
}

/** Couleur affichée des glyphes : Fabric peut la mettre dans `styles` plutôt que dans `fill` seul. */
function readITextEffectiveGlyphColor(t: fabric.Textbox | fabric.IText): string {
  const stylesUnknown = t.styles as
    | Record<string, Record<string, { fill?: string } | undefined> | undefined>
    | undefined;
  const text = t.text ?? "";
  if (stylesUnknown && text.length > 0) {
    const lines = text.split("\n");
    for (let li = 0; li < lines.length; li += 1) {
      const lineMap = stylesUnknown[String(li)] ?? stylesUnknown[li];
      if (!lineMap || typeof lineMap !== "object") {
        continue;
      }
      const line = lines[li] ?? "";
      for (let ci = 0; ci < line.length; ci += 1) {
        const cell = lineMap[String(ci)] ?? lineMap[ci];
        const f = cell?.fill;
        if (typeof f === "string" && f && !isFabricTextBackgroundTransparent(f)) {
          return f;
        }
      }
    }
  }
  const raw = t.fill;
  if (typeof raw === "string" && raw && !isFabricTextBackgroundTransparent(raw)) {
    return raw;
  }
  return "#111827";
}

function applyITextGlyphColor(canvas: fabric.Canvas, t: fabric.Textbox | fabric.IText, hex: string) {
  t.set({ fill: hex });
  const len = (t.text ?? "").length;
  if (len > 0) {
    t.setSelectionStyles({ fill: hex }, 0, len);
  }
  t.setCoords();
  canvas.requestRenderAll();
}

/** PDF.js n’accepte que des multiples de 90° pour la rotation du viewport. */
function normalizeViewportRotationDeg(deg: number): number {
  let r = Math.round(deg) % 360;
  if (r < 0) {
    r += 360;
  }
  return (Math.round(r / 90) * 90) % 360;
}

type PlacementState =
  | { type: "none" }
  | { type: "rect" }
  | { type: "text" }
  | { type: "line"; lineStart: { x: number; y: number } | null }
  | { type: "arrow"; lineStart: { x: number; y: number } | null };

function u3142NextPlacementAfterInsert(current: PlacementState): PlacementState {
  if (current.type === "line") {
    return { type: "line", lineStart: null };
  }
  if (current.type === "arrow") {
    return { type: "arrow", lineStart: null };
  }
  return current;
}

function u3142PageSegmentForPage1(pageNum1: number, segments: U3140DocumentPageSegment[]): U3140DocumentPageSegment | null {
  for (const s of segments) {
    if (pageNum1 >= s.startPage && pageNum1 <= s.endPage) {
      return s;
    }
  }
  return null;
}

/**
 * Entrée de la chaîne de lots du document : `lotDetailId` + plage de pages 0-based (dérivée
 * du couple `pageSegments[i]` / `lotPayloads[i]` renvoyé par GET). Sert à regrouper les shapes
 * par lot au PUT pour respecter la règle d’alignement `lotPayloads` (cf. BU314212).
 */
interface U3142LotChainEntry {
  lotDetailId: string;
  /** Bornes 0-based inclusives dans le PDF fusionné. `null` si pageSegments absent. */
  startPage0: number | null;
  endPage0: number | null;
}

/**
 * Construit la chaîne `lotChainRef` à partir des `lotPayloads` (ordre + lotDetailId) et des
 * `pageSegments` (plages de pages) renvoyés par le GET. Si `pageSegments` est absent, les
 * bornes restent `null` : la reconstruction du PUT tombera alors sur les stratégies de repli
 * (lot unique → toutes les shapes dans ce lot ; sinon répartition par position).
 */
function u3142BuildLotChain(
  lotPayloads: U3140DocumentPdfAnnotationsLotPayload[],
  pageSegments: U3140DocumentPageSegment[] | undefined
): U3142LotChainEntry[] {
  return lotPayloads.map((lp, i) => {
    const seg = pageSegments && pageSegments[i];
    if (seg && Number.isFinite(seg.startPage) && Number.isFinite(seg.endPage)) {
      return { lotDetailId: lp.lotDetailId, startPage0: seg.startPage - 1, endPage0: seg.endPage - 1 };
    }
    return { lotDetailId: lp.lotDetailId, startPage0: null, endPage0: null };
  });
}

/**
 * Reconstruit `lotPayloads` au moment du PUT en répartissant les shapes (pageIndex global du
 * PDF fusionné) dans le bon lot. Règles :
 * - Respecte l’ordre et les `lotDetailId` reçus au GET (exigence `BU314212`).
 * - Si pageSegments étaient fournis → filtre `shape.pageIndex ∈ [startPage0, endPage0]`.
 * - Sinon et 1 seul lot → toutes les shapes pour ce lot.
 * - Sinon (plusieurs lots, pas de pageSegments) → shapes vides pour tous les lots (cas
 *   défensif : on évite d’affecter arbitrairement à un lot faute de mapping fiable).
 */
function u3142BuildLotPayloadsForSave(
  chain: U3142LotChainEntry[],
  shapes: U3140PdfAnnotationShape[]
): U3140DocumentPdfAnnotationsLotPayload[] {
  if (chain.length === 0) return [];
  if (chain.length === 1) {
    return [{ lotDetailId: chain[0].lotDetailId, schemaVersion: 1, shapes: shapes.slice() }];
  }
  const hasRanges = chain.every((e) => e.startPage0 != null && e.endPage0 != null);
  return chain.map((e) => {
    if (!hasRanges || e.startPage0 == null || e.endPage0 == null) {
      return { lotDetailId: e.lotDetailId, schemaVersion: 1 as const, shapes: [] };
    }
    const sp = e.startPage0;
    const ep = e.endPage0;
    return {
      lotDetailId: e.lotDetailId,
      schemaVersion: 1 as const,
      shapes: shapes.filter((s) => s.pageIndex >= sp && s.pageIndex <= ep),
    };
  });
}

/** Total des shapes effectivement placées dans les lotPayloads (garde-fou pré-PUT). */
function u3142CountShapesInLotPayloads(lotPayloads: U3140DocumentPdfAnnotationsLotPayload[]): number {
  return lotPayloads.reduce((acc, lp) => acc + lp.shapes.length, 0);
}

/** Sérialisation canonique des shapes d’un segment pour comparer GET vs état courant (ordre neutre). */
function u3142StableShapesJson(shapes: U3140PdfAnnotationShape[]): string {
  if (shapes.length === 0) {
    return "[]";
  }
  const sorted = [...shapes].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  return JSON.stringify(sorted);
}

/** Lot de la chaîne contenant la page `pageIndex0` du PDF fusionné (pour marquer `annotated` après édition utilisateur). */
function u3142LotDetailIdForPage0(chain: U3142LotChainEntry[], pageIndex0: number): string | null {
  if (chain.length === 0) {
    return null;
  }
  if (chain.length === 1) {
    return chain[0].lotDetailId;
  }
  const hasRanges = chain.every((e) => e.startPage0 != null && e.endPage0 != null);
  if (!hasRanges) {
    return null;
  }
  for (const e of chain) {
    if (
      e.startPage0 != null &&
      e.endPage0 != null &&
      pageIndex0 >= e.startPage0 &&
      pageIndex0 <= e.endPage0
    ) {
      return e.lotDetailId;
    }
  }
  return null;
}

function u3142MarkAnnotatedLotEditedForPage0(
  chain: U3142LotChainEntry[],
  pageIndex0: number,
  editedLotDetailIds: Set<string>
): void {
  const id = u3142LotDetailIdForPage0(chain, pageIndex0);
  if (id) {
    editedLotDetailIds.add(id);
  }
}

/** Pages dont le contenu shapes a changé entre deux snapshots (undo/redo). */
function u3142PageIndicesDifferingBetweenShapes(
  prev: U3140PdfAnnotationShape[],
  next: U3140PdfAnnotationShape[]
): Set<number> {
  const mapPage = (arr: U3140PdfAnnotationShape[]) => {
    const m = new Map<number, U3140PdfAnnotationShape[]>();
    for (const s of arr) {
      const list = m.get(s.pageIndex) ?? [];
      list.push(s);
      m.set(s.pageIndex, list);
    }
    return m;
  };
  const mp = mapPage(prev);
  const mn = mapPage(next);
  const out = new Set<number>();
  const all = new Set([...mp.keys(), ...mn.keys()]);
  for (const p of all) {
    const a = mp.get(p) ?? [];
    const b = mn.get(p) ?? [];
    if (u3142StableShapesJson(a) !== u3142StableShapesJson(b)) {
      out.add(p);
    }
  }
  return out;
}

function u3142MarkAnnotatedLotsFromShapesDiff(
  chain: U3142LotChainEntry[],
  before: U3140PdfAnnotationShape[],
  after: U3140PdfAnnotationShape[],
  editedLotDetailIds: Set<string>
): void {
  for (const p of u3142PageIndicesDifferingBetweenShapes(before, after)) {
    u3142MarkAnnotatedLotEditedForPage0(chain, p, editedLotDetailIds);
  }
}

/** Snapshot par `lotDetailId` après GET ou après PUT réussi — comparé pour décider si un segment encore « dirty » mérite `annotated`. */
function u3142BuildLotBaselineMap(
  chain: U3142LotChainEntry[],
  shapes: U3140PdfAnnotationShape[]
): Map<string, string> {
  const payloads = u3142BuildLotPayloadsForSave(chain, shapes);
  const m = new Map<string, string>();
  for (const lp of payloads) {
    m.set(lp.lotDetailId, u3142StableShapesJson(lp.shapes));
  }
  return m;
}

/**
 * `annotated: true` uniquement si l’utilisateur a eu une action d’ajout / modification / suppression sur ce segment
 * (`editedLotDetailIds`) **et** que le contenu diffère encore du dernier GET / dernière sauvegarde (ex. annulation complète → pas d’annotated).
 */
function u3142ApplyAnnotatedFlagsForUserEditedLots(
  lotPayloads: U3140DocumentPdfAnnotationsLotPayload[],
  baseline: Map<string, string>,
  editedLotDetailIds: ReadonlySet<string>
): U3140DocumentPdfAnnotationsLotPayload[] {
  return lotPayloads.map((lp) => {
    const prev = baseline.get(lp.lotDetailId);
    const current = u3142StableShapesJson(lp.shapes);
    const touchedByUser = editedLotDetailIds.has(lp.lotDetailId);
    if (touchedByUser && current !== prev) {
      return { ...lp, annotated: true };
    }
    return {
      lotDetailId: lp.lotDetailId,
      schemaVersion: 1,
      shapes: lp.shapes,
    };
  });
}

/** Libellé segment / page : même règles que les pastilles concordance (pipes → tirets cadratin). */
function u3142FormatPdfPageSegmentLabel(
  label: string,
  parentContext?: U3141ConcordanceParentContext | null
): string {
  const raw = (label ?? "").trim();
  if (!raw) return "";
  const formatted = formatConcordanceEntryForDisplay(raw, parentContext ?? undefined).trim();
  return formatted.length > 0 ? formatted : raw;
}

type U3142PdfToolbarComboItem = Record<string, unknown> & { key: string; label: string };

function newClientId(): string {
  return `a_${Math.random().toString(36).slice(2, 11)}`;
}

function fabricObjectId(o: FabricObject): string {
  const anyO = o as U3142FabricObjectWithMetadata;
  if (!anyO._u3142Id) {
    anyO._u3142Id = newClientId();
  }
  return anyO._u3142Id;
}

function canvasToPdfRectNorm(
  left: number,
  top: number,
  w: number,
  h: number,
  vw: number,
  vh: number
): { x: number; y: number; w: number; h: number } {
  const x = left / vw;
  const y = 1 - (top + h) / vh;
  const wn = w / vw;
  const hn = h / vh;
  return { x, y, w: wn, h: hn };
}

/**
 * Boîte axis-aligned (px canvas, coin haut-gauche) d’un Rect, quel que soit originX/Y.
 * Nécessaire si un objet a encore l’origine `center` (défaut Fabric 7 avant patch module).
 */
function fabricRectCanvasTopLeftBox(obj: fabric.Rect): { left: number; top: number; w: number; h: number } {
  const w = (obj.width ?? 0) * (obj.scaleX ?? 1);
  const h = (obj.height ?? 0) * (obj.scaleY ?? 1);
  let left = obj.left ?? 0;
  let top = obj.top ?? 0;
  const ox = obj.originX ?? "left";
  const oy = obj.originY ?? "top";
  if (ox === "center") {
    left -= w / 2;
  } else if (ox === "right") {
    left -= w;
  } else if (typeof ox === "number") {
    left -= w * ox;
  }
  if (oy === "center") {
    top -= h / 2;
  } else if (oy === "bottom") {
    top -= h;
  } else if (typeof oy === "number") {
    top -= h * oy;
  }
  return { left, top, w, h };
}

function fabricRectToShape(obj: fabric.Rect, pageIndex: number, vw: number, vh: number): U3140PdfAnnotationShape {
  const box = fabricRectCanvasTopLeftBox(obj);
  const { x, y, w: wn, h: hn } = canvasToPdfRectNorm(box.left, box.top, box.w, box.h, vw, vh);
  const fill = typeof obj.fill === "string" ? obj.fill : "";
  const opacity = obj.opacity ?? 1;
  const transparentFill =
    !fill ||
    fill === "transparent" ||
    /^rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/i.test(fill) ||
    fill === "rgba(255,255,255,0)";
  return u3142ApplyAnnotationMetadataToShape({
    type: "rect",
    pageIndex,
    x,
    y,
    w: wn,
    h: hn,
    fillColor: transparentFill ? U3140_PDF_ANNOTATION_FILL_NONE : fill.startsWith("#") ? fill : fill || "#FFCC00",
    opacity,
    strokeColor: typeof obj.stroke === "string" ? obj.stroke : "#000000",
    strokeWidth: obj.strokeWidth ?? 0,
    layoutPageWidthPx: vw,
  }, obj);
}

/** Extrémités de la ligne en pixels canvas (repère haut-gauche). Ne pas utiliser left+x1 : Fabric.Line utilise un repère interne + transformations. */
function fabricLineEndpointsCanvasPx(l: fabric.Line): { x1: number; y1: number; x2: number; y2: number } {
  const line = l as fabric.Line & {
    calcLinePoints: () => { x1: number; y1: number; x2: number; y2: number };
  };
  const p = line.calcLinePoints();
  const matrix = line.calcTransformMatrix();
  const tp1 = fabric.util.transformPoint(new fabric.Point(p.x1, p.y1), matrix);
  const tp2 = fabric.util.transformPoint(new fabric.Point(p.x2, p.y2), matrix);
  return { x1: tp1.x, y1: tp1.y, x2: tp2.x, y2: tp2.y };
}

/**
 * Fabric positionne le repère de la `Line` avec une bbox qui inclut le trait : les extrémités
 * lues via `calcLinePoints` + matrice sont légèrement translatées par rapport aux pixels
 * passés au constructeur → dérive 0–1 à chaque rechargement/sauvegarde sans édition.
 * On recale `left`/`top` pour que les deux extrémités canvas coïncident avec la cible.
 */
function u3142NudgeFabricLineToCanvasEndpoints(
  ln: fabric.Line,
  wantX1: number,
  wantY1: number,
  wantX2: number,
  wantY2: number
): void {
  const EPS = 0.02;
  for (let i = 0; i < 3; i += 1) {
    ln.setCoords();
    const g = fabricLineEndpointsCanvasPx(ln);
    const dx = ((wantX1 - g.x1) + (wantX2 - g.x2)) / 2;
    const dy = ((wantY1 - g.y1) + (wantY2 - g.y2)) / 2;
    if (Math.abs(dx) < EPS && Math.abs(dy) < EPS) {
      return;
    }
    ln.set({ left: (ln.left ?? 0) + dx, top: (ln.top ?? 0) + dy });
  }
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function fabricLineToShape(obj: fabric.Line, pageIndex: number, vw: number, vh: number): U3140PdfAnnotationShape {
  const l = obj as fabric.Line;
  const { x1: x1a, y1: y1a, x2: x2a, y2: y2a } = fabricLineEndpointsCanvasPx(l);
  return u3142ApplyAnnotationMetadataToShape({
    type: "line",
    pageIndex,
    x1: clamp01(x1a / vw),
    y1: clamp01(1 - y1a / vh),
    x2: clamp01(x2a / vw),
    y2: clamp01(1 - y2a / vh),
    strokeColor: typeof l.stroke === "string" ? l.stroke : "#000000",
    strokeWidth: l.strokeWidth ?? 2,
    opacity: l.opacity ?? 1,
    layoutPageWidthPx: vw,
  }, obj);
}

/** Longueur nulle en 0–1 : aucun segment visible (ex. JSON x1=y1=x2=y2 ou double-clic placement ligne). */
const MIN_NORM_LINE_LEN_SQ = 1e-12;

function isRenderablePdfLineLikeShape(s: U3140PdfAnnotationShape): boolean {
  if (s.type !== "line" && s.type !== "arrow") {
    return true;
  }
  const dx = s.x2 - s.x1;
  const dy = s.y2 - s.y1;
  return dx * dx + dy * dy > MIN_NORM_LINE_LEN_SQ;
}

function dropDegenerateLineLikeShapes(shapes: U3140PdfAnnotationShape[]): U3140PdfAnnotationShape[] {
  return shapes.filter(isRenderablePdfLineLikeShape);
}

function isArrowFabricGroup(o: FabricObject): o is fabric.Group {
  return o instanceof fabric.Group && !!(o as fabric.Group & { u3142IsArrow?: boolean }).u3142IsArrow;
}

/** Bloc texte : rectangle de contour (cadre) + `Textbox` sans `stroke` sur les glyphes. */
function u3142IsTextFrameGroup(o: FabricObject | null | undefined): o is fabric.Group {
  return o instanceof fabric.Group && !!(o as fabric.Group & { u3142TextFrame?: boolean }).u3142TextFrame;
}

function u3142GetTextFrameMembers(grp: fabric.Group): { border: fabric.Rect; text: fabric.Textbox } | null {
  const objs = grp.getObjects();
  const border = objs[0];
  const text = objs[1];
  if (border instanceof fabric.Rect && text instanceof fabric.Textbox) {
    return { border, text };
  }
  return null;
}

/**
 * Entre en édition sur le Textbox d’un cadre (editable + enterEditing + focus textarea).
 * Fabric 7 : un `enterEditing` pendant `mousedown` peut laisser `isEditing` sans focus clavier
 * (geste navigateur) ; il faut pouvoir rappeler focus même si l’édition est déjà active.
 * Le groupe reste non-`interactive` (sélection / déplacement du cadre) — le clic natif sur le
 * Textbox est bloqué par Fabric 7 (`group && !group.interactive`).
 */
function u3142EnterTextFrameEditing(canvas: fabric.Canvas, grp: fabric.Group): void {
  const m = u3142GetTextFrameMembers(grp);
  if (!m) return;
  m.text.set({ editable: true });
  if (!m.text.isEditing) {
    m.text.enterEditing();
  }
  (m.text as fabric.IText).selectAll();
  m.text.hiddenTextarea?.focus();
  canvas.requestRenderAll();
}

/**
 * Synchronise la propriété `editable` des Textbox internes des groupes cadre texte en fonction
 * de l’objet actif du canvas. Seul le cadre actuellement sélectionné autorise l’entrée en mode
 * édition ; tous les autres cadres restent verrouillés et quittent le mode édition s’ils y étaient
 * encore. Quand le cadre actif est un groupe texte, l’entrée en édition est déclenchée
 * immédiatement (avec sélection de tout le texte) pour permettre à l’utilisateur d’écrire
 * directement après un simple clic de sélection.
 */
function u3142SyncTextFrameEditableFromSelection(canvas: fabric.Canvas): void {
  const active = canvas.getActiveObject();
  const activeIsFrame = !!active && u3142IsTextFrameGroup(active);
  canvas.getObjects().forEach((o: FabricObject) => {
    if (!u3142IsTextFrameGroup(o)) return;
    const m = u3142GetTextFrameMembers(o);
    if (!m) return;
    const shouldEdit = activeIsFrame && o === active;
    if (m.text.editable !== shouldEdit) {
      m.text.set({ editable: shouldEdit });
    }
    if (!shouldEdit && m.text.isEditing) {
      m.text.exitEditing();
    }
    if (shouldEdit && !m.text.isEditing) {
      u3142EnterTextFrameEditing(canvas, o);
    }
  });
}

/**
 * Pose le layout centré du groupe cadre texte pour les dimensions courantes.
 *
 * Invariant Fabric : dans un `Group`, les enfants sont positionnés **en coordonnées centrées
 * autour du centre du groupe** (cf. `group.class.js::_updateObjectsCoords`). Le rendu = `groupCenter + child.(left|top)`.
 * Avec `originY='top'`, `groupCenter.y = group.top + group.height/2`.
 *
 * Si on fait seulement `_calcBounds(true)` (mise à jour de `width/height` sans déplacer `top`),
 * le centre du groupe descend mais les enfants gardent leur `top` — ils se retrouvent dessinés
 * plus bas que le cadre de sélection. Correction : replacer explicitement les enfants à
 * `(-W/2, -H/2)` (+ padding pour le Textbox) et mettre `group.width/height = (W, H)`.
 */
function u3142SyncTextFrameBorderRect(grp: fabric.Group): void {
  const m = u3142GetTextFrameMembers(grp);
  if (!m) return;
  const { border, text } = m;
  const p = text.padding ?? 0;
  const tw = (text.width ?? 0) * (text.scaleX ?? 1);
  const minHPx = (text as U3142PdfTextboxAug)._u3142TextBoxMinHeightPx ?? 0;
  const contentH = text.calcTextHeight();
  const th = Math.max((text.height ?? 0) * (text.scaleY ?? 1), contentH, minHPx);
  const W = tw + 2 * p;
  const H = th + 2 * p;
  border.set({
    left: -W / 2,
    top: -H / 2,
    width: W,
    height: H,
    scaleX: 1,
    scaleY: 1,
    dirty: true,
  });
  text.set({
    left: -W / 2 + p,
    top: -H / 2 + p,
    dirty: true,
  });
  border.setCoords();
  text.setCoords();
  grp.set({ width: W, height: H, dirty: true });
  grp.setCoords();
}

function createU3142TextFrameGroup(opts: {
  text: string;
  left: number;
  top: number;
  originY: "top" | "bottom";
  width: number;
  fontSize: number;
  fill: string;
  backgroundColor: string;
  padding: number;
  strokeColor: string;
  strokeWidth: number;
  opacity: number;
  textAlign: U3140PdfAnnotationTextAlign | string;
  textBoxVerticalAlign: U3140PdfAnnotationTextBoxVerticalAlign;
  minHeightPx?: number;
}): fabric.Group {
  const pad = opts.padding;
  const tx = new fabric.Textbox(opts.text, {
    left: pad,
    top: pad,
    originX: "left",
    originY: "top",
    width: opts.width,
    fontSize: opts.fontSize,
    fill: opts.fill,
    backgroundColor: opts.backgroundColor,
    padding: pad,
    stroke: undefined,
    strokeWidth: 0,
    opacity: opts.opacity,
    selectable: true,
    objectCaching: false,
    /* Verrouillé par défaut ; activé uniquement quand le groupe cadre est l’objet actif (voir u3142SyncTextFrameEditableFromSelection). */
    editable: false,
    textAlign: (opts.textAlign as "left" | "center" | "right") || "left",
    splitByGrapheme: false,
  });
  fabricObjectId(tx);
  const aug = tx as U3142PdfTextboxAug;
  aug._u3142TextBoxVerticalAlign = u3142NormalizeTextBoxVerticalAlign(opts.textBoxVerticalAlign);
  if (opts.minHeightPx != null && opts.minHeightPx > 0) {
    aug._u3142TextBoxMinHeightPx = opts.minHeightPx;
  }
  u3142RefreshPdfTextboxLayout(tx);
  const strokeW = Math.max(0, Math.min(12, opts.strokeWidth));
  const hasStroke = strokeW > 0;
  const border = new fabric.Rect({
    left: 0,
    top: 0,
    originX: "left",
    originY: "top",
    width: 48,
    height: 48,
    fill: "rgba(0,0,0,0)",
    stroke: hasStroke ? opts.strokeColor : undefined,
    strokeWidth: hasStroke ? strokeW : 0,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  fabricObjectId(border);
  (border as fabric.Rect & { u3142TextBorder?: boolean }).u3142TextBorder = true;
  const grp = new fabric.Group([border, tx], {
    left: opts.left,
    top: opts.top,
    originX: "left",
    originY: opts.originY,
    subTargetCheck: true,
    opacity: opts.opacity,
    objectCaching: false,
  });
  (grp as fabric.Group & { u3142TextFrame?: boolean }).u3142TextFrame = true;
  u3142SyncTextFrameBorderRect(grp);
  return grp;
}

/**
 * Profondeur (px) de la tête de flèche le long de l’axe (pointe → base).
 * **Pilotée par l’épaisseur** du trait (`strokeWidth`) : H ≈ 2,4·sw + 3.
 * Bornée pour ne jamais dépasser ~45 % de la longueur (sinon la tête « mange » la tige).
 * Mêmes valeurs au placement, à la sérialisation et à la recréation après chargement.
 */
function u3142ArrowHeadDepthPx(fullLen: number, strokeWidth: number): number {
  const L = Math.max(1, fullLen);
  const sw = Math.max(1, strokeWidth);
  const H = 2.4 * sw + 3;
  return Math.max(6, Math.min(H, 48, L * 0.45));
}

/**
 * Largeur (px) du bord de base du chevron, perpendiculaire à l’axe.
 * **Pilotée par l’épaisseur** ; toujours **strictement supérieure** à `sw` pour que la tige
 * (épaisseur `sw`) entre intégralement dans la base et reste **centrée** sur le chevron.
 */
function u3142ArrowHeadBaseWidthPx(fullLen: number, strokeWidth: number, depthPx: number): number {
  const L = Math.max(1, fullLen);
  const sw = Math.max(1, strokeWidth);
  const target = 2.0 * sw + 2;
  const minVisible = sw + 2;
  const cap = Math.min(0.85 * depthPx, 0.4 * L, 40);
  return Math.max(minVisible, Math.min(target, cap));
}

type U3142WriteStrokeWidthContext = { vw: number; vh: number; pageIndex0: number };

/**
 * (x1,y1) = départ, (tipX, tipY) = pointe — la tige s’arrête en base (avant la pointe).
 * Retourne Bmid = centre du bord de base, et H = profondeur tête, pour nudge tige = Bmid.
 */
function u3142ArrowShaftBmid(
  x1: number,
  y1: number,
  tipX: number,
  tipY: number,
  strokeWidth: number
): { bmidX: number; bmidY: number; H: number; baseW: number; fullLen: number; ux: number; uy: number; px: number; py: number; tipX: number; tipY: number } {
  const dx = tipX - x1;
  const dy = tipY - y1;
  const fullLen = Math.sqrt(dx * dx + dy * dy) || 1;
  const ux = dx / fullLen;
  const uy = dy / fullLen;
  const H = u3142ArrowHeadDepthPx(fullLen, strokeWidth);
  const bmidX = tipX - ux * H;
  const bmidY = tipY - uy * H;
  const baseW = u3142ArrowHeadBaseWidthPx(fullLen, strokeWidth, H);
  const px = -uy;
  const py = ux;
  return { bmidX, bmidY, H, baseW, fullLen, ux, uy, px, py, tipX, tipY };
}

/**
 * Indices conventionnés des sommets dans le polygone-silhouette de flèche (7 points) :
 *
 *   Q1 (0) ───── Q2 (1)         B1 (2)
 *      │            │           ╲
 *      │   tige     │            ╲
 *      │            │             ▶ T (3) pointe
 *      │            │            ╱
 *   Q4 (6) ───── Q3 (5)         B2 (4)
 *
 * `(Q1+Q4)/2 = (x1,y1)` (centre arrière de la tige), `T = (x2,y2)` (pointe).
 * Distance Q1→Q4 = épaisseur (`strokeWidth`).
 */
const U3142_ARROW_VERTEX_Q1 = 0;
const U3142_ARROW_VERTEX_Q2 = 1;
const U3142_ARROW_VERTEX_B1 = 2;
const U3142_ARROW_VERTEX_TIP = 3;
const U3142_ARROW_VERTEX_B2 = 4;
const U3142_ARROW_VERTEX_Q3 = 5;
const U3142_ARROW_VERTEX_Q4 = 6;
const U3142_ARROW_VERTEX_COUNT = 7;
void U3142_ARROW_VERTEX_Q2;
void U3142_ARROW_VERTEX_B1;
void U3142_ARROW_VERTEX_B2;
void U3142_ARROW_VERTEX_Q3;

function isArrowSilhouettePolygon(o: FabricObject): o is fabric.Polygon {
  return (
    o instanceof fabric.Polygon &&
    !!(o as fabric.Polygon & { u3142ArrowSilhouette?: boolean }).u3142ArrowSilhouette
  );
}

/**
 * Coordonnées canvas absolues d’un sommet du polygone (gère `pathOffset` + matrice
 * groupe→canvas). Renvoie `null` si l’objet n’a pas encore de matrice valide.
 */
function arrowPolygonVertexInCanvasPx(poly: fabric.Polygon, idx: number): { x: number; y: number } | null {
  const pts = poly.points;
  if (!pts || pts.length <= idx) return null;
  const p = pts[idx]!;
  const off = poly.pathOffset ?? new fabric.Point(0, 0);
  const local = new fabric.Point(p.x - off.x, p.y - off.y);
  const m = poly.calcTransformMatrix();
  if (!m) return null;
  const tp = fabric.util.transformPoint(local, m);
  return { x: tp.x, y: tp.y };
}

function createArrowFabricGroup(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  stroke: string,
  strokeWidth: number
): fabric.Group {
  const g = u3142ArrowShaftBmid(x1, y1, x2, y2, strokeWidth);
  const halfSw = Math.max(1, strokeWidth) / 2;
  const halfBw = g.baseW / 2;
  const Q1 = { x: x1 + g.px * halfSw, y: y1 + g.py * halfSw };
  const Q2 = { x: g.bmidX + g.px * halfSw, y: g.bmidY + g.py * halfSw };
  const B1 = { x: g.bmidX + g.px * halfBw, y: g.bmidY + g.py * halfBw };
  const T = { x: g.tipX, y: g.tipY };
  const B2 = { x: g.bmidX - g.px * halfBw, y: g.bmidY - g.py * halfBw };
  const Q3 = { x: g.bmidX - g.px * halfSw, y: g.bmidY - g.py * halfSw };
  const Q4 = { x: x1 - g.px * halfSw, y: y1 - g.py * halfSw };

  const silhouette = new fabric.Polygon([Q1, Q2, B1, T, B2, Q3, Q4], {
    fill: stroke,
    stroke: stroke,
    strokeWidth: 0,
    opacity: 1,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  (silhouette as fabric.Polygon & { u3142ArrowSilhouette?: boolean }).u3142ArrowSilhouette = true;
  /** Épaisseur d’origine du trait, **invariante** aux scales appliqués au groupe (utilisée pour
   * reconstruire la flèche à la fin d’un redimensionnement sans grossir la tige ni la pointe). */
  (silhouette as fabric.Polygon & { u3142ArrowOriginalStrokeWidth?: number }).u3142ArrowOriginalStrokeWidth = Math.max(
    1,
    strokeWidth
  );
  fabricObjectId(silhouette);

  const grp = new fabric.Group([silhouette], {
    selectable: true,
    subTargetCheck: false,
    objectCaching: false,
    /* Pas de mise à l’échelle non uniforme involontaire : on accepte tout scale et on
       l’absorbe dans la géométrie à la fin de la transformation. */
    lockScalingFlip: true,
  });
  (grp as fabric.Group & { u3142IsArrow?: boolean }).u3142IsArrow = true;
  fabricObjectId(grp);
  u3142ConfigureLineLikeControls(grp);
  return grp;
}

/**
 * Récupère l’épaisseur **d’origine** du trait pour une flèche silhouette : conservée à la
 * création (`u3142ArrowOriginalStrokeWidth`), donc non affectée par les scales de groupe.
 * Repli : `|Q1-Q4|` en coordonnées **locales** au polygone (équivalent invariant au scale du groupe).
 */
function u3142GetArrowSilhouetteOriginalStrokeWidth(silhouette: fabric.Polygon): number {
  const stored = (silhouette as fabric.Polygon & { u3142ArrowOriginalStrokeWidth?: number })
    .u3142ArrowOriginalStrokeWidth;
  if (typeof stored === "number" && stored > 0) {
    return stored;
  }
  const pts = silhouette.points;
  if (pts && pts.length >= U3142_ARROW_VERTEX_COUNT) {
    const a = pts[U3142_ARROW_VERTEX_Q1]!;
    const b = pts[U3142_ARROW_VERTEX_Q4]!;
    const d = Math.hypot(a.x - b.x, a.y - b.y);
    if (d > 0) return d;
  }
  return 2;
}

/**
 * À la fin d’une transformation (scale/rotation) du groupe flèche : recalcule la silhouette à
 * partir des sommets `Q1, Q4, T` transformés en coordonnées canvas, en **conservant l’épaisseur
 * d’origine** du trait. La flèche garde donc sa tige et son chevron à la même taille, seuls
 * son point de départ, sa pointe et son orientation sont mis à jour.
 *
 * Implémentation : on remplace le groupe par un nouveau (`createArrowFabricGroup`) — c’est
 * plus sûr que de muter `polygon.points` + `group.scale = 1` à cause des nombreuses propriétés
 * Fabric à reconcilier (`pathOffset`, bbox, controls).
 */
function u3142NormalizeArrowFabricGroupAfterTransform(
  canvas: fabric.Canvas,
  grp: fabric.Group
): fabric.Group | null {
  if (!isArrowFabricGroup(grp)) return null;
  const sx = grp.scaleX ?? 1;
  const sy = grp.scaleY ?? 1;
  const angle = grp.angle ?? 0;
  if (Math.abs(sx - 1) < 1e-4 && Math.abs(sy - 1) < 1e-4 && Math.abs(angle) < 1e-4) {
    return null;
  }
  const silhouette = grp.getObjects().find(isArrowSilhouettePolygon);
  if (!silhouette) return null;
  const Q1c = arrowPolygonVertexInCanvasPx(silhouette, U3142_ARROW_VERTEX_Q1);
  const Q4c = arrowPolygonVertexInCanvasPx(silhouette, U3142_ARROW_VERTEX_Q4);
  const Tc = arrowPolygonVertexInCanvasPx(silhouette, U3142_ARROW_VERTEX_TIP);
  if (!Q1c || !Q4c || !Tc) return null;
  const startX = (Q1c.x + Q4c.x) / 2;
  const startY = (Q1c.y + Q4c.y) / 2;
  const swOriginal = u3142GetArrowSilhouetteOriginalStrokeWidth(silhouette);
  const fillCol = typeof silhouette.fill === "string" ? silhouette.fill : "#000000";
  const opacity = grp.opacity ?? 1;
  const wasActive = canvas.getActiveObject() === grp;
  canvas.remove(grp);
  const next = createArrowFabricGroup(startX, startY, Tc.x, Tc.y, fillCol, swOriginal);
  next.set({ opacity });
  canvas.add(next);
  if (wasActive) {
    canvas.setActiveObject(next);
  }
  next.setCoords();
  return next;
}

/**
 * Poignées Fabric pour ligne / flèche (H, V ou oblique) :
 * masque les coins (déformeraient le segment) ; active les milieux + rotation.
 */
function u3142ConfigureLineLikeControls(obj: FabricObject): void {
  obj.setControlsVisibility({
    ml: true,
    mr: true,
    mt: true,
    mb: true,
    tl: false,
    tr: false,
    bl: false,
    br: false,
    mtr: true,
  });
  obj.lockRotation = false;
}

/**
 * Reconstruit la pointe (canvas px) : la tige s’arrête en B ; le JSON stocke la pointe T.
 * Point fixe F ≈ Lb + u3142(F,sw) (Lb = tige) puis T = B + n·h(F,sw).
 * Anciennes flèches (Line + Triangle) : 2e extrémité tige = ancienne cible, on ne décale pas.
 */
function u3142ArrowTipCanvasForShapeFromLine(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  lineStrokeW: number,
  legacyNoPolygon: boolean
): { x: number; y: number } {
  if (legacyNoPolygon) {
    return p2;
  }
  const Lb = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1;
  const nx = (p2.x - p1.x) / Lb;
  const ny = (p2.y - p1.y) / Lb;
  let F = Lb + u3142ArrowHeadDepthPx(Lb, lineStrokeW);
  for (let k = 0; k < 3; k += 1) {
    F = Lb + u3142ArrowHeadDepthPx(F, lineStrokeW);
  }
  const h = u3142ArrowHeadDepthPx(F, lineStrokeW);
  return { x: p2.x + nx * h, y: p2.y + ny * h };
}

function fabricArrowGroupToShape(g: fabric.Group, pageIndex: number, vw: number, vh: number): U3140PdfAnnotationShape {
  const silhouette = g.getObjects().find(isArrowSilhouettePolygon);
  if (silhouette && (silhouette.points?.length ?? 0) >= U3142_ARROW_VERTEX_COUNT) {
    const Q1c = arrowPolygonVertexInCanvasPx(silhouette, U3142_ARROW_VERTEX_Q1);
    const Q4c = arrowPolygonVertexInCanvasPx(silhouette, U3142_ARROW_VERTEX_Q4);
    const Tc = arrowPolygonVertexInCanvasPx(silhouette, U3142_ARROW_VERTEX_TIP);
    if (Q1c && Q4c && Tc) {
      const startX = (Q1c.x + Q4c.x) / 2;
      const startY = (Q1c.y + Q4c.y) / 2;
      const sw = Math.max(1, Math.hypot(Q1c.x - Q4c.x, Q1c.y - Q4c.y));
      const fillCol = typeof silhouette.fill === "string" ? silhouette.fill : "#000000";
      return {
        ...u3142ReadAnnotationMetadataFromFabricObject(g),
        type: "arrow",
        pageIndex,
        x1: clamp01(startX / vw),
        y1: clamp01(1 - startY / vh),
        x2: clamp01(Tc.x / vw),
        y2: clamp01(1 - Tc.y / vh),
        strokeColor: fillCol,
        strokeWidth: sw,
        opacity: g.opacity ?? silhouette.opacity ?? 1,
        layoutPageWidthPx: vw,
      };
    }
  }
  const line = g.getObjects().find((o: FabricObject): o is fabric.Line => o instanceof fabric.Line);
  if (!line) {
    return {
      type: "arrow",
      pageIndex,
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 0,
      strokeColor: "#000000",
      strokeWidth: 2,
      opacity: 1,
    };
  }
  const { x1: x1a, y1: y1a, x2: x2a, y2: y2a } = fabricLineEndpointsCanvasPx(line);
  const sw = line.strokeWidth ?? 2;
  const p1c = { x: x1a, y: y1a };
  const p2c = { x: x2a, y: y2a };
  const hasPoly = g.getObjects().some((o: FabricObject) => o instanceof fabric.Polygon);
  const hasTri = g.getObjects().some((o: FabricObject) => o instanceof fabric.Triangle);
  const legacy = !hasPoly && hasTri;
  const tipC = u3142ArrowTipCanvasForShapeFromLine(p1c, p2c, sw, legacy);
  return {
    ...u3142ReadAnnotationMetadataFromFabricObject(g),
    type: "arrow",
    pageIndex,
    x1: clamp01(x1a / vw),
    y1: clamp01(1 - y1a / vh),
    x2: clamp01(tipC.x / vw),
    y2: clamp01(1 - tipC.y / vh),
    strokeColor: typeof line.stroke === "string" ? line.stroke : "#000000",
    strokeWidth: line.strokeWidth ?? 2,
    opacity: g.opacity ?? line.opacity ?? 1,
    layoutPageWidthPx: vw,
  };
}

function u3142NormalizeFabricTextAlign(raw: unknown): U3140PdfAnnotationTextAlign {
  if (raw === "center" || raw === "right") return raw;
  return "left";
}

type U3140PdfAnnotationTextShape = Extract<U3140PdfAnnotationShape, { type: "text" }>;

function u3142TextShapeUsesPxLayout(s: U3140PdfAnnotationTextShape): boolean {
  return s.fontSizePx != null && s.layoutPageWidthPx != null && s.layoutPageWidthPx > 0;
}

/** Taille Fabric (px canvas) à partir du JSON (spec : règle de trois si le viewport a changé). */
function u3142ResolveTextFontSizeOnCanvas(s: U3140PdfAnnotationTextShape, vw: number): number {
  if (u3142TextShapeUsesPxLayout(s)) {
    return s.fontSizePx! * (vw / s.layoutPageWidthPx!);
  }
  return s.fontSizePts ?? 14;
}

/** `y` JSON = bord haut (format px) ou explicite `textTop` ; sinon rétro = bas du cadre. */
function u3142TextShapeUsesTopY(s: U3140PdfAnnotationTextShape): boolean {
  return u3142TextShapeUsesPxLayout(s) || s.textVerticalAnchor === "textTop";
}

function fabricTextToShape(obj: fabric.Textbox | fabric.IText, pageIndex: number, vw: number, vh: number): U3140PdfAnnotationShape {
  const br = obj.getBoundingRect();
  const x = br.left / vw;
  const bg = typeof obj.backgroundColor === "string" ? obj.backgroundColor : "";
  const sw = obj.strokeWidth ?? 0;
  const align = u3142NormalizeFabricTextAlign(obj.textAlign);
  const isBox = obj instanceof fabric.Textbox;
  const widthNorm = u3142ClampTextBoxWidthNorm(br.width / vw);
  const color = readITextEffectiveGlyphColor(obj);
  const bgOut = isFabricTextBackgroundTransparent(bg) ? undefined : bg;
  const strokeColor = sw > 0 && typeof obj.stroke === "string" ? obj.stroke : undefined;
  const strokeWidth = sw > 0 ? sw : undefined;

  if (isBox) {
    const fontSizePx = Math.round((obj.fontSize ?? 14) * (obj.scaleY ?? 1));
    const yTop = 1 - br.top / vh;
    const aug = obj as U3142PdfTextboxAug;
    const valign = u3142NormalizeTextBoxVerticalAlign(aug._u3142TextBoxVerticalAlign);
    return u3142ApplyAnnotationMetadataToShape({
      type: "text",
      pageIndex,
      x,
      y: yTop,
      text: obj.text ?? "",
      fontSizePx,
      layoutPageWidthPx: Math.round(vw),
      textVerticalAnchor: "textTop",
      color,
      backgroundColor: bgOut,
      strokeColor,
      strokeWidth,
      opacity: obj.opacity ?? 1,
      textAlign: align === "left" ? undefined : align,
      textBoxWidth: widthNorm,
      textBoxHeight: u3142ClampTextBoxHeightNorm(br.height / vh),
      textBoxVerticalAlign: valign === "top" ? undefined : valign,
    }, obj);
  }

  const yBottom = 1 - (br.top + br.height) / vh;
  const fontSizePts = (obj.fontSize ?? 14) * Math.max(obj.scaleX ?? 1, obj.scaleY ?? 1);
  return u3142ApplyAnnotationMetadataToShape({
    type: "text",
    pageIndex,
    x,
    y: yBottom,
    text: obj.text ?? "",
    fontSizePts,
    color,
    backgroundColor: bgOut,
    strokeColor,
    strokeWidth,
    opacity: obj.opacity ?? 1,
    textAlign: align === "left" ? undefined : align,
    textBoxWidth: widthNorm,
  }, obj);
}

function fabricTextFrameGroupToShape(grp: fabric.Group, pageIndex: number, vw: number, vh: number): U3140PdfAnnotationShape {
  const m = u3142GetTextFrameMembers(grp);
  if (!m) {
    return {
      type: "text",
      pageIndex,
      x: 0,
      y: 1,
      text: "",
      fontSizePx: 14,
      layoutPageWidthPx: Math.round(vw),
      textVerticalAnchor: "textTop",
      color: "#111827",
      opacity: 1,
    };
  }
  const { border, text } = m;
  const br = grp.getBoundingRect();
  const x = br.left / vw;
  const bg = typeof text.backgroundColor === "string" ? text.backgroundColor : "";
  const sw = border.strokeWidth ?? 0;
  const align = u3142NormalizeFabricTextAlign(text.textAlign);
  const widthNorm = u3142ClampTextBoxWidthNorm(br.width / vw);
  const color = readITextEffectiveGlyphColor(text);
  const bgOut = isFabricTextBackgroundTransparent(bg) ? undefined : bg;
  const strokeColor = sw > 0 && typeof border.stroke === "string" ? border.stroke : undefined;
  const strokeWidth = sw > 0 ? sw : undefined;
  const fontSizePx = Math.round(text.fontSize ?? 14);
  const yTop = 1 - br.top / vh;
  const aug = text as U3142PdfTextboxAug;
  const valign = u3142NormalizeTextBoxVerticalAlign(aug._u3142TextBoxVerticalAlign);
  return u3142ApplyAnnotationMetadataToShape({
    type: "text",
    pageIndex,
    x,
    y: yTop,
    text: text.text ?? "",
    fontSizePx,
    layoutPageWidthPx: Math.round(vw),
    textVerticalAnchor: "textTop",
    color,
    backgroundColor: bgOut,
    strokeColor,
    strokeWidth,
    opacity: grp.opacity ?? text.opacity ?? 1,
    textAlign: align === "left" ? undefined : align,
    textBoxWidth: widthNorm,
    textBoxHeight: u3142ClampTextBoxHeightNorm(br.height / vh),
    textBoxVerticalAlign: valign === "top" ? undefined : valign,
  }, grp);
}

/** Cible unique pour les réglages (évite ActiveSelection multi-objets ambiguë). */
function getFabricTargetForStyle(active: FabricObject | undefined | null): FabricObject | null {
  if (!active) return null;
  if (active instanceof fabric.ActiveSelection) {
    const objs = active.getObjects();
    return objs.length === 1 ? objs[0]! : null;
  }
  return active;
}

type U3140PdfAnnotationSelectionKind = "none" | "rect" | "line" | "arrow" | "text";

interface U3140PdfAnnotationSelectionStyleSnapshot {
  kind: Exclude<U3140PdfAnnotationSelectionKind, "none">;
  /** Remplissage du rectangle, ou fond du bloc texte (`backgroundColor`). */
  fillColor: string;
  /** Couleur des glyphes (texte uniquement). */
  textColor?: string;
  strokeColor: string;
  strokeWidth: number;
  opacity: number;
  fontSizePts: number;
  /** Texte uniquement (`IText.textAlign`). */
  textAlign?: U3140PdfAnnotationTextAlign;
  /** Texte : largeur du bloc / viewport (0–1). */
  textBoxWidthNorm?: number;
  /** Texte : alignement vertical dans le cadre. */
  textBoxVerticalAlign?: U3140PdfAnnotationTextBoxVerticalAlign;
}

type U3140PdfAnnotationShapeKind = "rect" | "line" | "arrow" | "text";

interface U3140PdfAnnotationToolbarStyle {
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  opacity: number;
  textColor: string;
  fontSizePts: number;
  /** Utilisé pour le type texte ; ignoré pour rect/ligne/flèche. */
  textAlign: U3140PdfAnnotationTextAlign;
  /** Largeur du Textbox / viewport (0–1), type texte uniquement. */
  textBoxWidthNorm: number;
  /** Alignement vertical du texte dans le cadre (Textbox). */
  textBoxVerticalAlign: U3140PdfAnnotationTextBoxVerticalAlign;
  defaultWidthPx?: number;
  defaultHeightPx?: number;
  defaultWidthNorm?: number;
  defaultHeightNorm?: number;
}

function u3142DefaultToolbarStyleBase(): U3140PdfAnnotationToolbarStyle {
  return {
    fillColor: "#FFCC00",
    strokeColor: "#000000",
    strokeWidth: 2,
    opacity: 0.55,
    textColor: "#111827",
    fontSizePts: 14,
    textAlign: "left",
    textBoxWidthNorm: U3142_PDF_TEXT_BOX_WIDTH_DEFAULT_NORM,
    textBoxVerticalAlign: "top",
  };
}

function u3142InitialDefaultsByKind(): Record<U3140PdfAnnotationShapeKind, U3140PdfAnnotationToolbarStyle> {
  const base = u3142DefaultToolbarStyleBase();
  const lineLike: U3140PdfAnnotationToolbarStyle = {
    ...base,
    fillColor: U3140_PDF_ANNOTATION_FILL_NONE,
  };
  const textDefaults: U3140PdfAnnotationToolbarStyle = {
    ...base,
    fillColor: U3140_PDF_ANNOTATION_FILL_NONE,
    textColor: "#000000",
    strokeWidth: 0,
    fontSizePts: 24,
    opacity: 1,
  };
  /** Remplissage par défaut du rectangle (bleu). */
  const rectFillBlue = "#2563EB";
  return {
    rect: { ...base, fillColor: rectFillBlue, opacity: 1 },
    line: { ...lineLike },
    arrow: { ...lineLike },
    text: textDefaults,
  };
}

/** Met à jour le paquet de défauts pour un kind à partir d’une lecture Fabric (champs texte seulement si kind === text). */
function patchToolbarDefaultsFromSnap(
  kind: U3140PdfAnnotationShapeKind,
  snap: U3140PdfAnnotationSelectionStyleSnapshot,
  prev: U3140PdfAnnotationToolbarStyle
): U3140PdfAnnotationToolbarStyle {
  const next: U3140PdfAnnotationToolbarStyle = {
    ...prev,
    fillColor: snap.fillColor,
    strokeColor: snap.strokeColor,
    strokeWidth: snap.strokeWidth,
    opacity: snap.opacity,
  };
  if (kind === "text") {
    next.textColor = snap.textColor ?? prev.textColor;
    next.fontSizePts = snap.fontSizePts;
    next.textAlign = snap.textAlign ?? "left";
    next.textBoxWidthNorm = snap.textBoxWidthNorm ?? prev.textBoxWidthNorm;
    next.textBoxVerticalAlign = snap.textBoxVerticalAlign ?? prev.textBoxVerticalAlign;
  }
  return next;
}

function getKindForToolbarDefaultPersistence(
  canvas: fabric.Canvas | null | undefined,
  placement: PlacementState
): U3140PdfAnnotationShapeKind | null {
  if (canvas) {
    const snap = readFabricSelectionStyle(canvas.getActiveObject());
    if (snap) return snap.kind;
  }
  if (placement.type === "rect") return "rect";
  if (placement.type === "text") return "text";
  if (placement.type === "line") return "line";
  if (placement.type === "arrow") return "arrow";
  return null;
}

/**
 * Pour la barre d'outils partagée : le **mode de placement** prime sur l'objet Fabric encore
 * sélectionné, afin de mettre à jour le bon paquet (ex. `defaultsByKind.rect`) quand l'utilisateur
 * prépare un rectangle alors qu'une ligne est encore active en sélection.
 */
function getKindForSharedToolbarDefaults(
  canvas: fabric.Canvas | null | undefined,
  placement: PlacementState
): U3140PdfAnnotationShapeKind | null {
  if (placement.type === "rect") return "rect";
  if (placement.type === "text") return "text";
  if (placement.type === "line") return "line";
  if (placement.type === "arrow") return "arrow";
  return getKindForToolbarDefaultPersistence(canvas, placement);
}

function getKindFromFabricStyleTarget(t: FabricObject | null | undefined): U3140PdfAnnotationShapeKind | null {
  if (!t) return null;
  if (t instanceof fabric.Rect) return "rect";
  if (t instanceof fabric.Line) return "line";
  if (isArrowFabricGroup(t)) return "arrow";
  if (u3142IsTextFrameGroup(t) || t instanceof fabric.IText) return "text";
  return null;
}

function readFabricSelectionStyle(active: FabricObject | undefined | null): U3140PdfAnnotationSelectionStyleSnapshot | null {
  const t = getFabricTargetForStyle(active);
  if (!t) return null;
  if (u3142IsTextFrameGroup(t)) {
    const m = u3142GetTextFrameMembers(t);
    if (!m) return null;
    const text = m.text;
    const glyph = readITextEffectiveGlyphColor(text);
    const bg = typeof text.backgroundColor === "string" ? text.backgroundColor : "";
    const sw = m.border.strokeWidth ?? 0;
    const strokeCol = typeof m.border.stroke === "string" ? m.border.stroke : "#000000";
    const cw =
      text.canvas && typeof (text.canvas as fabric.Canvas).getWidth === "function"
        ? (text.canvas as fabric.Canvas).getWidth()
        : (text.canvas?.width ?? 400);
    const textBoxWidthNorm = u3142ClampTextBoxWidthNorm(text.getScaledWidth() / Math.max(1, cw));
    const aug = text as U3142PdfTextboxAug;
    return {
      kind: "text",
      fillColor: isFabricTextBackgroundTransparent(bg) ? U3140_PDF_ANNOTATION_FILL_NONE : bg,
      textColor: glyph,
      strokeColor: strokeCol,
      strokeWidth: sw,
      opacity: t.opacity ?? text.opacity ?? 1,
      fontSizePts: text.fontSize ?? 14,
      textAlign: u3142NormalizeFabricTextAlign(text.textAlign),
      textBoxWidthNorm,
      textBoxVerticalAlign: u3142NormalizeTextBoxVerticalAlign(aug._u3142TextBoxVerticalAlign),
    };
  }
  if (t instanceof fabric.Rect) {
    const fill = typeof t.fill === "string" ? t.fill : "";
    const transparentFill =
      !fill ||
      fill === "transparent" ||
      /^rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/i.test(fill) ||
      fill === "rgba(255,255,255,0)";
    return {
      kind: "rect",
      fillColor: transparentFill ? U3140_PDF_ANNOTATION_FILL_NONE : fill.startsWith("#") ? fill : fill || "#FFCC00",
      strokeColor: typeof t.stroke === "string" ? t.stroke : "#000000",
      strokeWidth: t.strokeWidth ?? 0,
      opacity: t.opacity ?? 1,
      fontSizePts: 14,
    };
  }
  if (t instanceof fabric.Line) {
    return {
      kind: "line",
      fillColor: U3140_PDF_ANNOTATION_FILL_NONE,
      strokeColor: typeof t.stroke === "string" ? t.stroke : "#000000",
      strokeWidth: t.strokeWidth ?? 2,
      opacity: t.opacity ?? 1,
      fontSizePts: 14,
    };
  }
  if (isArrowFabricGroup(t)) {
    const silhouette = t.getObjects().find(isArrowSilhouettePolygon);
    if (silhouette) {
      const Q1c = arrowPolygonVertexInCanvasPx(silhouette, U3142_ARROW_VERTEX_Q1);
      const Q4c = arrowPolygonVertexInCanvasPx(silhouette, U3142_ARROW_VERTEX_Q4);
      const sw =
        Q1c && Q4c ? Math.max(1, Math.hypot(Q1c.x - Q4c.x, Q1c.y - Q4c.y)) : 2;
      return {
        kind: "arrow",
        fillColor: U3140_PDF_ANNOTATION_FILL_NONE,
        strokeColor: typeof silhouette.fill === "string" ? silhouette.fill : "#000000",
        strokeWidth: sw,
        opacity: t.opacity ?? 1,
        fontSizePts: 14,
      };
    }
    const line = t.getObjects().find((c: FabricObject): c is fabric.Line => c instanceof fabric.Line);
    return {
      kind: "arrow",
      fillColor: U3140_PDF_ANNOTATION_FILL_NONE,
      strokeColor: line && typeof line.stroke === "string" ? line.stroke : "#000000",
      strokeWidth: line?.strokeWidth ?? 2,
      opacity: t.opacity ?? 1,
      fontSizePts: 14,
    };
  }
  if (t instanceof fabric.IText) {
    const glyph = readITextEffectiveGlyphColor(t);
    const bg = typeof t.backgroundColor === "string" ? t.backgroundColor : "";
    let sw = t.strokeWidth ?? 0;
    let strokeCol = typeof t.stroke === "string" ? t.stroke : "#000000";
    if (t.group && u3142IsTextFrameGroup(t.group)) {
      const m = u3142GetTextFrameMembers(t.group);
      if (m) {
        sw = m.border.strokeWidth ?? 0;
        strokeCol = typeof m.border.stroke === "string" ? m.border.stroke : "#000000";
      }
    }
    const cw =
      t.canvas && typeof (t.canvas as fabric.Canvas).getWidth === "function"
        ? (t.canvas as fabric.Canvas).getWidth()
        : (t.canvas?.width ?? 400);
    const textBoxWidthNorm = u3142ClampTextBoxWidthNorm(t.getScaledWidth() / Math.max(1, cw));
    const isBox = t instanceof fabric.Textbox;
    const aug = isBox ? (t as U3142PdfTextboxAug) : null;
    return {
      kind: "text",
      fillColor: isFabricTextBackgroundTransparent(bg) ? U3140_PDF_ANNOTATION_FILL_NONE : bg,
      textColor: glyph,
      strokeColor: strokeCol,
      strokeWidth: sw,
      opacity: t.opacity ?? 1,
      fontSizePts: isBox ? (t.fontSize ?? 14) * (t.scaleY ?? 1) : (t.fontSize ?? 14) * Math.max(t.scaleX ?? 1, t.scaleY ?? 1),
      textAlign: u3142NormalizeFabricTextAlign(t.textAlign),
      textBoxWidthNorm,
      textBoxVerticalAlign: aug ? u3142NormalizeTextBoxVerticalAlign(aug._u3142TextBoxVerticalAlign) : undefined,
    };
  }
  return null;
}

function writeFillToFabricSelection(canvas: fabric.Canvas, target: FabricObject, fillHexOrTransparent: string) {
  const transparent = fillHexOrTransparent === U3140_PDF_ANNOTATION_FILL_NONE;
  if (u3142IsTextFrameGroup(target)) {
    const m = u3142GetTextFrameMembers(target);
    if (m) {
      writeFillToFabricSelection(canvas, m.text, fillHexOrTransparent);
      u3142SyncTextFrameBorderRect(target);
    }
    return;
  }
  if (target instanceof fabric.Rect) {
    target.set("fill", transparent ? "rgba(0,0,0,0)" : fillHexOrTransparent);
    target.setCoords();
    canvas.requestRenderAll();
    return;
  }
  if (target instanceof fabric.IText) {
    if (transparent) {
      target.set({ backgroundColor: "", padding: 0 });
    } else {
      target.set({ backgroundColor: fillHexOrTransparent, padding: 4 });
    }
    target.setCoords();
    if (target.group && u3142IsTextFrameGroup(target.group)) {
      u3142SyncTextFrameBorderRect(target.group);
    }
    canvas.requestRenderAll();
  }
}

function writeStrokeToFabricSelection(canvas: fabric.Canvas, target: FabricObject, color: string) {
  if (u3142IsTextFrameGroup(target)) {
    const m = u3142GetTextFrameMembers(target);
    if (m) {
      const sw = (m.border.strokeWidth ?? 0) <= 0 ? 2 : (m.border.strokeWidth ?? 2);
      m.border.set({ stroke: color, strokeWidth: sw });
      u3142SyncTextFrameBorderRect(target);
      target.setCoords();
      canvas.requestRenderAll();
    }
    return;
  }
  if (target instanceof fabric.Rect) {
    const sw = (target.strokeWidth ?? 0) <= 0 ? 2 : (target.strokeWidth ?? 2);
    target.set({ stroke: color, strokeWidth: sw });
    target.setCoords();
    canvas.requestRenderAll();
    return;
  }
  if (target instanceof fabric.IText) {
    if (target.group && u3142IsTextFrameGroup(target.group)) {
      writeStrokeToFabricSelection(canvas, target.group, color);
      return;
    }
    const sw = (target.strokeWidth ?? 0) <= 0 ? 2 : (target.strokeWidth ?? 2);
    target.set({ stroke: color, strokeWidth: sw });
    target.setCoords();
    canvas.requestRenderAll();
    return;
  }
  if (target instanceof fabric.Line) {
    const sw = (target.strokeWidth ?? 0) <= 0 ? 2 : (target.strokeWidth ?? 2);
    target.set({ stroke: color, strokeWidth: sw });
    target.setCoords();
    canvas.requestRenderAll();
    return;
  }
  if (isArrowFabricGroup(target)) {
    for (const ch of target.getObjects()) {
      if (ch instanceof fabric.Polygon) ch.set({ fill: color, stroke: color });
      else if (ch instanceof fabric.Line) ch.set({ stroke: color });
      else if (ch instanceof fabric.Triangle) ch.set({ fill: color, stroke: color });
    }
    target.set({ dirty: true });
    target.setCoords();
    canvas.requestRenderAll();
  }
}

function u3142ReplaceArrowGroupForStrokeWidth(
  canvas: fabric.Canvas,
  grp: fabric.Group,
  w: number,
  ctx: U3142WriteStrokeWidthContext
) {
  const s = fabricArrowGroupToShape(grp, ctx.pageIndex0, ctx.vw, ctx.vh);
  if (s.type !== "arrow") {
    return;
  }
  const width = Math.max(0, Math.min(12, w));
  const x1 = s.x1 * ctx.vw;
  const y1 = (1 - s.y1) * ctx.vh;
  const x2 = s.x2 * ctx.vw;
  const y2 = (1 - s.y2) * ctx.vh;
  const wasActive = canvas.getActiveObject() === grp;
  canvas.remove(grp);
  const n = createArrowFabricGroup(
    x1,
    y1,
    x2,
    y2,
    s.strokeColor ?? "#000000",
    width <= 0 ? 1 : Math.max(1, width)
  );
  n.set({ opacity: s.opacity ?? 1 });
  canvas.add(n);
  if (wasActive) {
    canvas.setActiveObject(n);
  }
  n.setCoords();
  canvas.requestRenderAll();
}

function writeStrokeWidthToFabricSelection(
  canvas: fabric.Canvas,
  target: FabricObject,
  w: number,
  arrowCtx?: U3142WriteStrokeWidthContext
) {
  const width = Math.max(0, Math.min(12, w));
  if (u3142IsTextFrameGroup(target)) {
    const m = u3142GetTextFrameMembers(target);
    if (m) {
      m.border.set({
        strokeWidth: width,
        stroke: width > 0 ? (typeof m.border.stroke === "string" ? m.border.stroke : "#000000") : undefined,
      });
      u3142SyncTextFrameBorderRect(target);
      target.setCoords();
      canvas.requestRenderAll();
    }
    return;
  }
  if (target instanceof fabric.Rect) {
    target.set({
      strokeWidth: width,
      stroke: width > 0 ? (typeof target.stroke === "string" ? target.stroke : "#000000") : undefined,
    });
    target.setCoords();
    canvas.requestRenderAll();
    return;
  }
  if (target instanceof fabric.Line) {
    target.set({ strokeWidth: width <= 0 ? 0 : Math.max(1, width) });
    target.setCoords();
    canvas.requestRenderAll();
    return;
  }
  if (target instanceof fabric.IText) {
    if (target.group && u3142IsTextFrameGroup(target.group)) {
      writeStrokeWidthToFabricSelection(canvas, target.group, w);
      return;
    }
    target.set({
      strokeWidth: width,
      stroke: width > 0 ? (typeof target.stroke === "string" ? target.stroke : "#000000") : undefined,
    });
    target.setCoords();
    canvas.requestRenderAll();
    return;
  }
  if (isArrowFabricGroup(target)) {
    if (arrowCtx && arrowCtx.vw > 0 && arrowCtx.vh > 0) {
      u3142ReplaceArrowGroupForStrokeWidth(canvas, target, w, arrowCtx);
      return;
    }
    const line = target.getObjects().find((c: FabricObject): c is fabric.Line => c instanceof fabric.Line);
    if (line) {
      line.set({ strokeWidth: width <= 0 ? 1 : Math.max(1, width) });
    }
    target.set({ dirty: true });
    target.setCoords();
    canvas.requestRenderAll();
  }
}

function writeOpacityToFabricSelection(canvas: fabric.Canvas, target: FabricObject, alpha: number) {
  const a = Math.min(1, Math.max(0.05, alpha));
  if (u3142IsTextFrameGroup(target)) {
    const grp = target as fabric.Group;
    grp.set({ opacity: a });
    for (const ch of grp.getObjects()) {
      ch.set({ opacity: a });
    }
    grp.setCoords();
    canvas.requestRenderAll();
    return;
  }
  if (target instanceof fabric.IText && target.group && u3142IsTextFrameGroup(target.group)) {
    writeOpacityToFabricSelection(canvas, target.group, alpha);
    return;
  }
  if (isArrowFabricGroup(target)) {
    target.set({ opacity: a });
    for (const ch of target.getObjects()) {
      ch.set({ opacity: a });
    }
  } else {
    target.set({ opacity: a });
  }
  target.setCoords();
  canvas.requestRenderAll();
}

function writeFontSizeToFabricSelection(canvas: fabric.Canvas, target: FabricObject, pts: number) {
  if (u3142IsTextFrameGroup(target)) {
    const m = u3142GetTextFrameMembers(target);
    if (m) {
      writeFontSizeToFabricSelection(canvas, m.text, pts);
      u3142SyncTextFrameBorderRect(target);
    }
    return;
  }
  if (!(target instanceof fabric.IText)) return;
  const fs = Math.min(120, Math.max(6, Math.round(Number(pts) || 0)));
  target.set({ fontSize: fs, scaleX: 1, scaleY: 1 });
  if (target instanceof fabric.Textbox) {
    u3142RefreshPdfTextboxLayout(target);
  }
  target.setCoords();
  canvas.requestRenderAll();
}

/**
 * Après redimensionnement aux poignées : pour un `Textbox`, absorber `scaleX` dans la
 * **largeur** du bloc (retours à la ligne) sans toucher à la taille de police ; remettre
 * `scaleY` à 1 pour ne pas mélanger zoom vertical et `fontSize`.
 */
function u3142NormalizeFabricTextBoxAfterTransform(t: fabric.Textbox): boolean {
  const sx = t.scaleX ?? 1;
  const sy = t.scaleY ?? 1;
  if (Math.abs(sx - 1) < 1e-4 && Math.abs(sy - 1) < 1e-4) {
    return false;
  }
  const ext = t as U3142PdfTextboxAug;
  const nextW = Math.max(40, (t.width ?? 0) * sx);
  const contentH = t.calcTextHeight();
  const baseH = (t.height ?? 0) > 1 ? (t.height ?? contentH) : contentH;
  const scaledH = Math.max(contentH, baseH * sy);
  ext._u3142TextBoxMinHeightPx = scaledH;
  t.set({ width: nextW, height: scaledH, scaleX: 1, scaleY: 1 });
  u3142RefreshPdfTextboxLayout(t);
  return true;
}

/**
 * Après redimensionnement aux poignées du **groupe** cadre texte : absorber `scaleX`/`scaleY`
 * du groupe dans la largeur / hauteur du `Textbox` interne (comme pour un Textbox seul),
 * puis remettre l’échelle du groupe à 1 — sinon le texte paraît « zoomé » (confondu avec la taille de police).
 */
function u3142NormalizeTextFrameGroupAfterTransform(grp: fabric.Group): boolean {
  const gsx = grp.scaleX ?? 1;
  const gsy = grp.scaleY ?? 1;
  if (Math.abs(gsx - 1) < 1e-4 && Math.abs(gsy - 1) < 1e-4) {
    return false;
  }
  const m = u3142GetTextFrameMembers(grp);
  if (!m) return false;
  const text = m.text;
  const tsx = text.scaleX ?? 1;
  const tsy = text.scaleY ?? 1;
  const nextW = Math.max(40, (text.width ?? 0) * tsx * gsx);
  const contentH = text.calcTextHeight();
  const baseH = (text.height ?? 0) > 1 ? (text.height ?? contentH) : contentH;
  const scaledH = Math.max(contentH, baseH * tsy * gsy);
  const aug = text as U3142PdfTextboxAug;
  aug._u3142TextBoxMinHeightPx = scaledH;
  text.set({ width: nextW, height: scaledH, scaleX: 1, scaleY: 1 });
  grp.set({ scaleX: 1, scaleY: 1 });
  u3142RefreshPdfTextboxLayout(text);
  grp.setCoords();
  text.setCoords();
  return true;
}

function serializeFabricPage(canvas: fabric.Canvas, pageIndex: number, vw: number, vh: number): U3140PdfAnnotationShape[] {
  const out: U3140PdfAnnotationShape[] = [];
  for (const o of canvas.getObjects()) {
    if (isArrowFabricGroup(o)) {
      const arrowShape = fabricArrowGroupToShape(o, pageIndex, vw, vh);
      if (isRenderablePdfLineLikeShape(arrowShape)) {
        out.push(arrowShape);
      }
    } else if (u3142IsTextFrameGroup(o)) {
      out.push(fabricTextFrameGroupToShape(o, pageIndex, vw, vh));
    } else if (o instanceof fabric.Rect) {
      out.push(fabricRectToShape(o, pageIndex, vw, vh));
    } else if (o instanceof fabric.Line) {
      const lineShape = fabricLineToShape(o, pageIndex, vw, vh);
      if (isRenderablePdfLineLikeShape(lineShape)) {
        out.push(lineShape);
      }
    } else if (o instanceof fabric.IText) {
      out.push(fabricTextToShape(o, pageIndex, vw, vh));
    }
  }
  return out;
}

function u3142CloneShapes(shapes: U3140PdfAnnotationShape[]): U3140PdfAnnotationShape[] {
  return JSON.parse(JSON.stringify(shapes)) as U3140PdfAnnotationShape[];
}

/** `strokeWidth` stocké = px éditeur d’origine ; si le canvas actuel a une autre largeur, rescale. */
function u3142ScaleStoredStrokeWidthToCanvasVw(
  storedStrokePx: number,
  layoutPageWidthPx: number | undefined,
  currentVw: number
): number {
  if (layoutPageWidthPx != null && layoutPageWidthPx > 0 && currentVw > 0) {
    return (storedStrokePx * currentVw) / layoutPageWidthPx;
  }
  return storedStrokePx;
}

function applyShapesToCanvas(
  canvas: fabric.Canvas,
  shapes: U3140PdfAnnotationShape[],
  pageIndex: number,
  vw: number,
  vh: number
) {
  canvas.clear();
  const forPage = shapes.filter((s) => s.pageIndex === pageIndex);
  for (const s of forPage) {
    const shapeMetadata = u3142ExtractAnnotationMetadataFromShape(s);
    if (s.type === "rect") {
      const left = s.x * vw;
      const top = (1 - s.y - s.h) * vh;
      const w = s.w * vw;
      const h = s.h * vh;
      const fillNone = s.fillColor === U3140_PDF_ANNOTATION_FILL_NONE || !s.fillColor;
      const rectSw = u3142ScaleStoredStrokeWidthToCanvasVw(s.strokeWidth ?? 0, s.layoutPageWidthPx, vw);
      const r = new fabric.Rect({
        left,
        top,
        originX: "left",
        originY: "top",
        width: w,
        height: h,
        fill: fillNone ? "rgba(0,0,0,0)" : (s.fillColor ?? "rgba(255,204,0,0.45)"),
        stroke: rectSw ? s.strokeColor ?? "#000000" : undefined,
        strokeWidth: rectSw,
        opacity: s.opacity ?? 1,
        selectable: true,
      });
      fabricObjectId(r);
      u3142AttachAnnotationMetadataOnFabricObject(r, shapeMetadata);
      canvas.add(r);
    } else if (s.type === "line") {
      if (!isRenderablePdfLineLikeShape(s)) {
        continue;
      }
      const x1 = s.x1 * vw;
      const y1 = (1 - s.y1) * vh;
      const x2 = s.x2 * vw;
      const y2 = (1 - s.y2) * vh;
      const lineSw = u3142ScaleStoredStrokeWidthToCanvasVw(s.strokeWidth ?? 2, s.layoutPageWidthPx, vw);
      const ln = new fabric.Line([x1, y1, x2, y2], {
        stroke: s.strokeColor ?? "#000000",
        strokeWidth: lineSw,
        opacity: s.opacity ?? 1,
        selectable: true,
      });
      fabricObjectId(ln);
      u3142AttachAnnotationMetadataOnFabricObject(ln, shapeMetadata);
      canvas.add(ln);
      u3142NudgeFabricLineToCanvasEndpoints(ln, x1, y1, x2, y2);
      u3142ConfigureLineLikeControls(ln);
    } else if (s.type === "arrow") {
      if (!isRenderablePdfLineLikeShape(s)) {
        continue;
      }
      const x1 = s.x1 * vw;
      const y1 = (1 - s.y1) * vh;
      const x2 = s.x2 * vw;
      const y2 = (1 - s.y2) * vh;
      const arrowSw = u3142ScaleStoredStrokeWidthToCanvasVw(s.strokeWidth ?? 2, s.layoutPageWidthPx, vw);
      const grp = createArrowFabricGroup(x1, y1, x2, y2, s.strokeColor ?? "#000000", arrowSw);
      grp.set({ opacity: s.opacity ?? 1 });
      u3142AttachAnnotationMetadataOnFabricObject(grp, shapeMetadata);
      canvas.add(grp);
      u3142ConfigureLineLikeControls(grp);
    } else if (s.type === "text") {
      const st = s;
      const left = st.x * vw;
      const bg = st.backgroundColor;
      const bgNone = !bg || bg === U3140_PDF_ANNOTATION_FILL_NONE || isFabricTextBackgroundTransparent(bg);
      const pad = bgNone ? 0 : 4;
      const sw = st.strokeWidth ?? 0;
      const wNorm = st.textBoxWidth ?? U3142_PDF_TEXT_BOX_WIDTH_DEFAULT_NORM;
      const boxW = Math.min(Math.max(0, vw - left), Math.max(48, u3142ClampTextBoxWidthNorm(wNorm) * vw));
      const fontSizeCanvas = u3142ResolveTextFontSizeOnCanvas(st, vw);
      const useTopY = u3142TextShapeUsesTopY(st);
      const hNorm = st.textBoxHeight;
      const minHPx =
        hNorm != null && hNorm > 0
          ? Math.max(fontSizeCanvas * 1.2, u3142ClampTextBoxHeightNorm(hNorm) * vh)
          : undefined;
      const grp = createU3142TextFrameGroup({
        text: st.text,
        left,
        top: (1 - st.y) * vh,
        originY: useTopY ? "top" : "bottom",
        width: boxW,
        fontSize: fontSizeCanvas,
        fill: st.color ?? "#000000",
        backgroundColor: bgNone ? "" : (bg ?? ""),
        padding: pad,
        strokeColor: st.strokeColor ?? "#000000",
        strokeWidth: sw,
        opacity: st.opacity ?? 1,
        textAlign: st.textAlign ?? "left",
        textBoxVerticalAlign: u3142NormalizeTextBoxVerticalAlign(st.textBoxVerticalAlign),
        minHeightPx: minHPx,
      });
      u3142AttachAnnotationMetadataOnFabricObject(grp, shapeMetadata);
      canvas.add(grp);
      const inner = u3142GetTextFrameMembers(grp)?.text;
      if (inner) {
        applyITextGlyphColor(canvas, inner, st.color ?? "#000000");
        u3142RefreshPdfTextboxLayout(inner);
        u3142SyncTextFrameBorderRect(grp);
      }
    }
  }
  canvas.requestRenderAll();
}

export interface U3140PdfAnnotationEditorDialogProps {
  isOpen: boolean;
  documentId: string | null;
  /** Contexte du lot ouvert (district / cadastre) pour formatter les `label` comme la concordance. */
  concordanceParentContext?: U3141ConcordanceParentContext | null;
  onClose: () => void;
  onSaved?: () => void;
  /** Préfixe API document / annotations (`U3141` enregistre foncier, `U3142` lot org.). */
  documentLogicalUnit: U3140DocumentLogicalUnit;
  /** Namespace i18next pour les clés de l’éditeur (ex. `u3140`). */
  i18nNamespace?: string;
  /** Rôle IAM pour afficher la gestion des préréglages (ex. `U3141:EDITPRESET`). */
  presetEditRole: string;
  /** Overlay plein écran (ex. `z-120` pour empiler au-dessus d’une autre modale comme le graphe U3141). */
  overlayClassName?: string;
}

export const U3140PdfAnnotationEditorDialog: React.FC<U3140PdfAnnotationEditorDialogProps> = ({
  isOpen,
  documentId,
  concordanceParentContext = null,
  onClose,
  onSaved,
  documentLogicalUnit,
  i18nNamespace = "u3140",
  presetEditRole,
  overlayClassName,
}) => {
  /** Dialogue principal avec overlay relevé (ex. z-120 au-dessus du graphe U3141) : les modales enfants doivent être au-dessus. */
  const nestedDialogsElevated = Boolean(overlayClassName);
  const toolbarPresetDialogOverlayClassName = nestedDialogsElevated ? "z-130" : undefined;
  const presetDeleteConfirmOverlayClassName = nestedDialogsElevated ? "z-140" : "z-110";
  const discardConfirmOverlayClassName = nestedDialogsElevated ? "z-145" : "z-100";

  const { t } = useTranslation(i18nNamespace);
  const { hasRole } = useU2000AuthContext();
  const toast = useToast();
  const { loadOriginalPdfBuffer } = useU3140PdfDocumentBuffer(documentLogicalUnit);
  const pdfCanvasRef = useRef<HTMLCanvasElement>(null);
  /** Hôte DOM pour Fabric 7 (évite de réutiliser un <canvas> géré par React après dispose async). */
  const fabricHostRef = useRef<HTMLDivElement>(null);
  const pdfScrollAreaRef = useRef<HTMLDivElement>(null);
  /** Dernière largeur pour laquelle le ResizeObserver a déclenché `renderPdfPage` (le scale PDF ne dépend que de la largeur utile). */
  const pdfScrollAreaWidthLastRenderRef = useRef(0);
  const fabricRef = useRef<fabric.Canvas | null>(null);
  const pdfDocRef = useRef<import("pdfjs-dist").PDFDocumentProxy | null>(null);
  const viewportSizeRef = useRef<{ vw: number; vh: number }>({ vw: 0, vh: 0 });
  const shapesRef = useRef<U3140PdfAnnotationShape[]>([]);
  const lotChainRef = useRef<U3142LotChainEntry[]>([]);
  const undoStackRef = useRef<U3140PdfAnnotationShape[][]>([]);
  const redoStackRef = useRef<U3140PdfAnnotationShape[][]>([]);
  const savedBaselineJsonRef = useRef<string>("[]");
  /** Baseline par segment (lot) pour `annotated` au PUT : dernier GET ou dernière sauvegarde réussie. */
  const baselineLotShapesJsonRef = useRef<Map<string, string>>(new Map());
  /** Lots ayant reçu au moins une action utilisateur ajout / modification / suppression (ou undo/redo impactant la page). */
  const annotatedEditedLotDetailIdsRef = useRef<Set<string>>(new Set());
  /** Pendant `applyShapesToCanvas` : ignorer les événements Fabric (hydratation calque). */
  const suppressAnnotatedDirtyRef = useRef(false);
  const applyingHistoryRef = useRef(false);
  const suppressHistoryRef = useRef(false);
  const historyIdleRef = useRef(true);
  const historyBatchBeforeRef = useRef<U3140PdfAnnotationShape[] | null>(null);
  const historyTimerRef = useRef<number | null>(null);
  const deleteSelectionRef = useRef<() => void>(() => {});
  /** Incrémenté à chaque nouvelle demande de rendu : ignore les `getPage` / `render` d’une navigation précédente (sinon page « retournée » ou mélangée). */
  const pdfRenderSeqRef = useRef(0);
  /** Chaîne pour sérialiser les rendus (évite dispose/init Fabric concurrents). */
  const pdfRenderChainRef = useRef<Promise<void>>(Promise.resolve());
  const pdfActiveRenderTaskRef = useRef<{ cancel: () => void; promise: Promise<unknown> } | null>(null);
  const pageNumRef = useRef(1);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [hasFabricSelection, setHasFabricSelection] = useState(false);
  const [historyStackSizes, setHistoryStackSizes] = useState({ undo: 0, redo: 0 });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [hoveredAnnotationMeta, setHoveredAnnotationMeta] = useState<U3142AnnotationFabricMetadata | null>(null);
  const [pageNum, setPageNum] = useState(1);
  pageNumRef.current = pageNum;
  const [numPages, setNumPages] = useState(0);
  const [fillColor, setFillColor] = useState("#FFCC00");
  const [textColor, setTextColor] = useState("#111827");
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [opacity, setOpacity] = useState(0.55);
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [fontSizePts, setFontSizePts] = useState(14);
  const [textAlign, setTextAlign] = useState<U3140PdfAnnotationTextAlign>("left");
  const [, setTextBoxWidthNorm] = useState(U3142_PDF_TEXT_BOX_WIDTH_DEFAULT_NORM);
  const [textBoxVerticalAlign, setTextBoxVerticalAlign] = useState<U3140PdfAnnotationTextBoxVerticalAlign>("top");
  /** Type d’objet Fabric sélectionné ; synchronisé pour activer/désactiver les contrôles et appliquer les changements sur la sélection. */
  const [annotationSelectionKind, setAnnotationSelectionKind] = useState<U3140PdfAnnotationSelectionKind>("none");
  /** Segments de pages (fusion) renvoyés par GET …/annotations ; vide si le backend ne les fournit pas encore. */
  const [pageSegments, setPageSegments] = useState<U3140DocumentPageSegment[]>([]);
  const [toolbarPresets, setToolbarPresets] = useState<U3140PdfAnnotationToolbarPreset[]>([]);
  /** Dernier préréglage barre d’outils appliqué (liste latérale) pour afficher la sélection. */
  const [appliedToolbarPresetId, setAppliedToolbarPresetId] = useState<string | null>(null);
  const [presetCreateOpen, setPresetCreateOpen] = useState(false);
  const [presetLabelDraft, setPresetLabelDraft] = useState("");
  const [presetHintDraft, setPresetHintDraft] = useState("");
  const [presetStylesDraft, setPresetStylesDraft] = useState<U3140PdfAnnotationToolbarPresetStyles>(() =>
    u3140MergeToolbarPresetStyles(undefined)
  );
  /** Modes du dialogue préréglage : modifier un existant ou créer un nouveau. */
  const [presetDialogMode, setPresetDialogMode] = useState<"edit" | "create">("edit");
  const [presetEditId, setPresetEditId] = useState<string | null>(null);
  const [createSourcePresetId, setCreateSourcePresetId] = useState<string | null>(null);
  const [presetRenameOpen, setPresetRenameOpen] = useState(false);
  const [presetDeleteConfirmOpen, setPresetDeleteConfirmOpen] = useState(false);
  const [presetSaving, setPresetSaving] = useState(false);
  /** Incrémenté à chaque ouverture du dialogue préréglage pour figer la baseline initiale. */
  const [presetBaselineSession, setPresetBaselineSession] = useState(0);
  const presetDialogBaselineRef = useRef<U3142PresetDialogBaseline | null>(null);
  /**
   * Décalage d’affichage supplémentaire (0/90/180/270) par **numéro de page (1-based)**.
   * Chaque page peut avoir un `/Rotate` différent après fusion ; un seul offset global ne suffit pas.
   */
  const [placement, setPlacement] = useState<PlacementState>({ type: "none" });
  const placementRef = useRef<PlacementState>(placement);
  placementRef.current = placement;

  const [defaultsByKind, setDefaultsByKind] = useState<Record<U3140PdfAnnotationShapeKind, U3140PdfAnnotationToolbarStyle>>(
    () => u3142InitialDefaultsByKind()
  );
  const defaultsByKindRef = useRef(defaultsByKind);
  defaultsByKindRef.current = defaultsByKind;
  const toolbarPresetsRef = useRef(toolbarPresets);
  toolbarPresetsRef.current = toolbarPresets;
  const appliedToolbarPresetIdRef = useRef(appliedToolbarPresetId);
  appliedToolbarPresetIdRef.current = appliedToolbarPresetId;

  const placementToolbarBootstrapKey = useMemo(() => {
    if (placement.type === "none") return "none";
    if (placement.type === "line" || placement.type === "arrow") {
      return placement.lineStart ? `${placement.type}:seg` : `${placement.type}:ready`;
    }
    return placement.type;
  }, [placement]);

  /** Au choix d’un outil de placement (hors 2e clic ligne/flèche), afficher les défauts mémorisés pour ce type. */
  useEffect(() => {
    if (placementToolbarBootstrapKey === "none") return;
    if (placementToolbarBootstrapKey.endsWith(":seg")) return;
    const kind: U3140PdfAnnotationShapeKind =
      placementToolbarBootstrapKey === "rect"
        ? "rect"
        : placementToolbarBootstrapKey === "text"
          ? "text"
          : placementToolbarBootstrapKey.startsWith("line")
            ? "line"
            : "arrow";
    const s = defaultsByKindRef.current[kind];
    setFillColor(s.fillColor);
    setTextColor(s.textColor);
    setStrokeColor(s.strokeColor);
    setStrokeWidth(s.strokeWidth);
    setOpacity(s.opacity);
    setFontSizePts(s.fontSizePts);
    setTextAlign(s.textAlign);
    setTextBoxWidthNorm(s.textBoxWidthNorm);
    setTextBoxVerticalAlign(s.textBoxVerticalAlign ?? "top");
  }, [placementToolbarBootstrapKey]);

  const goPlacement = useCallback((next: PlacementState) => {
    placementRef.current = next;
    setPlacement(next);
  }, []);

  const goPlacementRef = useRef(goPlacement);
  goPlacementRef.current = goPlacement;

  const tRef = useRef(t);
  tRef.current = t;

  const { loadAnnotations, saveAnnotations } = useU3140UsePdfAnnotationHandlers({
    documentLogicalUnit,
    i18nNamespace,
  });
  const { loadPresets, createPreset, updatePreset, deletePreset } = useU3140UsePdfAnnotationToolbarPresetHandlers({
    i18nNamespace,
  });

  const mergeShapesForPage = useCallback((pageIndex0: number, vw: number, vh: number, canvas: fabric.Canvas) => {
    const serialized = serializeFabricPage(canvas, pageIndex0, vw, vh);
    const next = [...shapesRef.current.filter((s) => s.pageIndex !== pageIndex0), ...serialized];

    if (!applyingHistoryRef.current && !suppressHistoryRef.current) {
      if (historyIdleRef.current) {
        historyBatchBeforeRef.current = u3142CloneShapes(shapesRef.current);
        historyIdleRef.current = false;
      }
      if (historyTimerRef.current != null) {
        window.clearTimeout(historyTimerRef.current);
      }
      historyTimerRef.current = window.setTimeout(() => {
        historyTimerRef.current = null;
        historyIdleRef.current = true;
        const before = historyBatchBeforeRef.current;
        historyBatchBeforeRef.current = null;
        if (before != null && JSON.stringify(before) !== JSON.stringify(shapesRef.current)) {
          undoStackRef.current.push(before);
          if (undoStackRef.current.length > U3142_PDF_ANNOTATION_HISTORY_MAX) {
            undoStackRef.current.shift();
          }
          redoStackRef.current = [];
          setHistoryStackSizes({
            undo: undoStackRef.current.length,
            redo: redoStackRef.current.length,
          });
        }
      }, U3142_PDF_ANNOTATION_HISTORY_DEBOUNCE_MS);
    }

    shapesRef.current = next;
  }, []);

  const mergeActivePageShapesFromCanvas = useCallback(() => {
    const canvas = fabricRef.current;
    const { vw, vh } = viewportSizeRef.current;
    if (canvas && vw > 0 && vh > 0) {
      mergeShapesForPage(pageNum - 1, vw, vh, canvas);
      /* Changements de style / toolbar sans nouvel événement Fabric `object:modified` → même règle qu’une édition explicite. */
      if (!suppressHistoryRef.current && !applyingHistoryRef.current) {
        u3142MarkAnnotatedLotEditedForPage0(
          lotChainRef.current,
          pageNum - 1,
          annotatedEditedLotDetailIdsRef.current
        );
      }
    }
  }, [pageNum, mergeShapesForPage]);

  const syncStyleUIFromFabricSelection = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) {
      setAnnotationSelectionKind("none");
      return;
    }
    const snap = readFabricSelectionStyle(canvas.getActiveObject());
    if (!snap) {
      setAnnotationSelectionKind("none");
      return;
    }
    setAnnotationSelectionKind(snap.kind);
    setFillColor(snap.fillColor);
    if (snap.kind === "text") {
      setTextColor(snap.textColor ?? "#111827");
      setTextBoxWidthNorm(snap.textBoxWidthNorm ?? U3142_PDF_TEXT_BOX_WIDTH_DEFAULT_NORM);
      setTextBoxVerticalAlign(snap.textBoxVerticalAlign ?? "top");
    }
    setStrokeColor(snap.strokeColor);
    setStrokeWidth(snap.strokeWidth);
    setOpacity(snap.opacity);
    setFontSizePts(snap.fontSizePts);
    setTextAlign(snap.textAlign ?? "left");
    setDefaultsByKind((prev) => ({
      ...prev,
      [snap.kind]: patchToolbarDefaultsFromSnap(snap.kind, snap, prev[snap.kind]),
    }));
  }, []);

  const syncStyleUIFromFabricSelectionRef = useRef(syncStyleUIFromFabricSelection);
  syncStyleUIFromFabricSelectionRef.current = syncStyleUIFromFabricSelection;

  const persistShapesForCurrentPdfPage = useCallback(() => {
    const canvas = fabricRef.current;
    const { vw, vh } = viewportSizeRef.current;
    if (!canvas || vw <= 0 || vh <= 0) return;
    const pageIndex0 = pageNum - 1;
    const serialized = serializeFabricPage(canvas, pageIndex0, vw, vh);
    shapesRef.current = [...shapesRef.current.filter((s) => s.pageIndex !== pageIndex0), ...serialized];
  }, [pageNum]);

  const pdfSegmentComboData = useMemo((): U3142PdfToolbarComboItem[] => {
    return pageSegments.map((s) => ({
      key: String(s.startPage),
      label: t("lblPdfAnnotationSegmentOption", {
        label: u3142FormatPdfPageSegmentLabel(s.label, concordanceParentContext),
        start: s.startPage,
        end: s.endPage,
      }),
    }));
  }, [pageSegments, t, concordanceParentContext]);

  const pdfPageComboData = useMemo((): U3142PdfToolbarComboItem[] => {
    return Array.from({ length: numPages }, (_, i) => {
      const n = i + 1;
      const seg = u3142PageSegmentForPage1(n, pageSegments);
      const label =
        seg != null
          ? t("lblPdfAnnotationPageOptionWithSegment", {
              page: n,
              segment: u3142FormatPdfPageSegmentLabel(seg.label, concordanceParentContext),
            })
          : String(n);
      return { key: String(n), label };
    });
  }, [numPages, pageSegments, t, concordanceParentContext]);

  const pdfSegmentComboHandlers = useMemo((): U2000FormStateHandlers<{ pdfSegment: string | null }> => {
    const noop = () => {};
    return {
      /** Toujours synchronisé avec `pageNum` : le segment affiché suit la navigation (page ou boutons prev/next). */
      getData: () => {
        const seg = u3142PageSegmentForPage1(pageNum, pageSegments);
        return { pdfSegment: seg != null ? String(seg.startPage) : null };
      },
      getTouched: () => ({}),
      getErrors: () => ({}),
      isSubmitted: () => false,
      getFormState: () => U2000FormStateEnumState.EDIT,
      setEditMode: noop,
      setData: noop,
      saveData: noop,
      getOldData: () => null,
      afterSaveData: noop,
      cancelData: noop,
      getFormEditMode: () => U2000FormStateEnumEditMode.ADD,
      markTouched: noop,
      setErrors: noop,
      setOtherErrors: noop,
      getOtherErrors: () => ({}),
      setSubmitted: noop,
      isValid: () => true,
      getCustomAttributes: () => ({}),
      setCustomAttributes: noop,
      onRecordChange: noop,
      onPageChange: noop,
      onLeave: noop,
      getBusinessHandlers: () => ({}),
      onChange: (_e, value) => {
        if (value == null || value === "") return;
        const target = Number(value);
        if (!Number.isFinite(target) || target < 1) return;
        persistShapesForCurrentPdfPage();
        goPlacement({ type: "none" });
        setPageNum(target);
      },
      onBlur: noop,
    };
  }, [pageNum, pageSegments, persistShapesForCurrentPdfPage, goPlacement]);

  const pdfPageComboHandlers = useMemo((): U2000FormStateHandlers<{ pdfPage: number }> => {
    const noop = () => {};
    return {
      getData: () => ({ pdfPage: pageNum }),
      getTouched: () => ({}),
      getErrors: () => ({}),
      isSubmitted: () => false,
      getFormState: () => U2000FormStateEnumState.EDIT,
      setEditMode: noop,
      setData: noop,
      saveData: noop,
      getOldData: () => null,
      afterSaveData: noop,
      cancelData: noop,
      getFormEditMode: () => U2000FormStateEnumEditMode.ADD,
      markTouched: noop,
      setErrors: noop,
      setOtherErrors: noop,
      getOtherErrors: () => ({}),
      setSubmitted: noop,
      isValid: () => true,
      getCustomAttributes: () => ({}),
      setCustomAttributes: noop,
      onRecordChange: noop,
      onPageChange: noop,
      onLeave: noop,
      getBusinessHandlers: () => ({}),
      onChange: (_e, value) => {
        if (value == null || value === "") return;
        const next = Number(value);
        if (!Number.isFinite(next) || next < 1) return;
        if (next === pageNum) return;
        persistShapesForCurrentPdfPage();
        goPlacement({ type: "none" });
        setPageNum(next);
      },
      onBlur: noop,
    };
  }, [pageNum, persistShapesForCurrentPdfPage, goPlacement]);

  const presetToolbarComboData = useMemo((): U3142PdfToolbarComboItem[] => {
    return toolbarPresets.map((p) => ({
      key: String(p.id),
      label: p.label,
    }));
  }, [toolbarPresets]);
  const hasExistingPresets = presetToolbarComboData.length > 0;

  const presetEditComboHandlers = useMemo((): U2000FormStateHandlers<{ presetEdit: string | null }> => {
    const noop = () => {};
    return {
      getData: () => ({ presetEdit: presetEditId }),
      getTouched: () => ({}),
      getErrors: () => ({}),
      isSubmitted: () => false,
      getFormState: () => U2000FormStateEnumState.EDIT,
      setEditMode: noop,
      setData: noop,
      saveData: noop,
      getOldData: () => null,
      afterSaveData: noop,
      cancelData: noop,
      getFormEditMode: () => U2000FormStateEnumEditMode.ADD,
      markTouched: noop,
      setErrors: noop,
      setOtherErrors: noop,
      getOtherErrors: () => ({}),
      setSubmitted: noop,
      isValid: () => true,
      getCustomAttributes: () => ({}),
      setCustomAttributes: noop,
      onRecordChange: noop,
      onPageChange: noop,
      onLeave: noop,
      getBusinessHandlers: () => ({}),
      onChange: (_e, value) => {
        if (value == null || value === "") {
          setPresetEditId(null);
          setPresetLabelDraft("");
          setPresetHintDraft("");
          setPresetRenameOpen(false);
          setPresetStylesDraft(u3140MergeToolbarPresetStyles(undefined));
          return;
        }
        const id = String(value);
        setPresetEditId(id);
        const p = toolbarPresets.find((x) => String(x.id) === id);
        if (p) {
          setPresetLabelDraft(p.label);
          setPresetHintDraft(p.hint ?? "");
          setPresetRenameOpen(false);
          setPresetStylesDraft(u3140MergeToolbarPresetStyles(p.styles));
        }
      },
      onBlur: noop,
    };
  }, [presetEditId, toolbarPresets]);

  const presetCreateSourceComboHandlers = useMemo((): U2000FormStateHandlers<{ createSource: string | null }> => {
    const noop = () => {};
    return {
      getData: () => ({ createSource: createSourcePresetId }),
      getTouched: () => ({}),
      getErrors: () => ({}),
      isSubmitted: () => false,
      getFormState: () => U2000FormStateEnumState.EDIT,
      setEditMode: noop,
      setData: noop,
      saveData: noop,
      getOldData: () => null,
      afterSaveData: noop,
      cancelData: noop,
      getFormEditMode: () => U2000FormStateEnumEditMode.ADD,
      markTouched: noop,
      setErrors: noop,
      setOtherErrors: noop,
      getOtherErrors: () => ({}),
      setSubmitted: noop,
      isValid: () => true,
      getCustomAttributes: () => ({}),
      setCustomAttributes: noop,
      onRecordChange: noop,
      onPageChange: noop,
      onLeave: noop,
      getBusinessHandlers: () => ({}),
      onChange: (_e, value) => {
        if (value == null || value === "") {
          setCreateSourcePresetId(null);
          setPresetStylesDraft(u3140MergeToolbarPresetStyles(undefined));
          return;
        }
        const id = String(value);
        setCreateSourcePresetId(id);
        const p = toolbarPresets.find((x) => String(x.id) === id);
        if (p) {
          setPresetStylesDraft(u3140MergeToolbarPresetStyles(p.styles));
          setPresetHintDraft(p.hint ?? "");
        }
      },
      onBlur: noop,
    };
  }, [createSourcePresetId, toolbarPresets]);

  const handleNewPresetMode = useCallback(() => {
    setPresetDialogMode("create");
    setPresetEditId(null);
    setCreateSourcePresetId(null);
    setPresetRenameOpen(false);
    setPresetLabelDraft("");
    setPresetHintDraft("");
    setPresetStylesDraft(
      u3140MergeToolbarPresetStyles({ rect: u3140NewPresetRectStyleSeed() as U3140PdfAnnotationToolbarPresetStyles["rect"] })
    );
  }, []);

  const handleCreateModeDefaultValues = useCallback(() => {
    setCreateSourcePresetId(null);
    setPresetHintDraft("");
    setPresetStylesDraft(
      u3140MergeToolbarPresetStyles({ rect: u3140NewPresetRectStyleSeed() as U3140PdfAnnotationToolbarPresetStyles["rect"] })
    );
  }, []);

  const handleReloadPresetFormSource = useCallback(() => {
    if (presetDialogMode === "edit" && presetEditId != null) {
      const p = toolbarPresets.find((x) => String(x.id) === presetEditId);
      if (p) {
        setPresetLabelDraft(p.label);
        setPresetHintDraft(p.hint ?? "");
        setPresetStylesDraft(u3140MergeToolbarPresetStyles(p.styles));
      }
    } else if (presetDialogMode === "create" && createSourcePresetId != null) {
      const p = toolbarPresets.find((x) => String(x.id) === createSourcePresetId);
      if (p) {
        setPresetStylesDraft(u3140MergeToolbarPresetStyles(p.styles));
        setPresetHintDraft(p.hint ?? "");
      }
    }
  }, [presetDialogMode, presetEditId, createSourcePresetId, toolbarPresets]);

  const isPresetFormVisible = presetDialogMode === "create" || (presetDialogMode === "edit" && presetEditId != null && presetEditId !== "");
  const showPresetResetBtn =
    hasExistingPresets &&
    (presetDialogMode === "create" || (presetDialogMode === "edit" && presetEditId != null && presetEditId !== ""));
  const showPresetNewBtn = hasExistingPresets && presetDialogMode === "edit" && (presetEditId == null || presetEditId === "");

  const goToAdjacentPdfPage = useCallback(
    (delta: -1 | 1) => {
      const next = pageNum + delta;
      if (numPages === 0 || !Number.isFinite(next) || next < 1 || next > numPages) {
        return;
      }
      persistShapesForCurrentPdfPage();
      goPlacement({ type: "none" });
      setPageNum(next);
    },
    [pageNum, numPages, persistShapesForCurrentPdfPage, goPlacement]
  );

  const disposeFabric = useCallback(async () => {
    const current = fabricRef.current;
    fabricRef.current = null;
    if (typeof window !== "undefined") {
      delete (window as Window & { __U3140_FABRIC_CANVAS__?: unknown }).__U3140_FABRIC_CANVAS__;
    }
    if (current) {
      try {
        await current.dispose();
      } catch {
        /* ignore double-dispose */
      }
    }
    const host = fabricHostRef.current;
    if (host) {
      host.replaceChildren();
      host.removeAttribute("style");
    }
  }, []);

  const renderPdfPage = useCallback(
    async (pageNumber: number, shapesArg: U3140PdfAnnotationShape[]) => {
      const run = async () => {
      const pdf = pdfDocRef.current;
      const pdfCanvas = pdfCanvasRef.current;
      const fabricHost = fabricHostRef.current;
      if (!pdf || !pdfCanvas || !fabricHost) return;

      const seq = ++pdfRenderSeqRef.current;
      /* pdfjs 6 : attendre la fin du cancel avant un nouveau render (évite #canvasInUse). */
      const previousTask = pdfActiveRenderTaskRef.current;
      pdfActiveRenderTaskRef.current = null;
      if (previousTask) {
        try {
          previousTask.cancel();
        } catch {
          /* ignore */
        }
        try {
          await previousTask.promise;
        } catch {
          /* RenderingCancelledException attendu */
        }
      }
      if (seq !== pdfRenderSeqRef.current) return;

      try {
        const page = await pdf.getPage(pageNumber);
        if (seq !== pdfRenderSeqRef.current) return;

        const rotation = normalizeViewportRotationDeg(page.rotate);
        const unscaled = page.getViewport({ scale: 1, rotation });
        let areaW = pdfScrollAreaRef.current?.clientWidth ?? 0;
        if (areaW < 120) {
          areaW = Math.floor(window.innerWidth * 0.92);
        }
        const targetW = Math.max(200, areaW - PDF_AREA_MARGIN_PX);
        const fitScale = targetW / unscaled.width;
        /* Toujours coller à la largeur utile (pas de plancher 0.85 qui déborde ou laisse des bandes). */
        const scale = Math.min(PDF_SCALE_MAX, Math.max(PDF_SCALE_MIN, fitScale));
        const viewport = page.getViewport({ scale, rotation });
        /* Largeur CSS entière pour éviter les gouttières liées au sous-pixel. */
        const vw = Math.max(1, Math.floor(viewport.width));
        const vh = Math.max(1, Math.floor(viewport.height));
        viewportSizeRef.current = { vw, vh };

        pdfCanvas.width = vw;
        pdfCanvas.height = vh;

        const ctx = pdfCanvas.getContext("2d", { alpha: false });
        if (!ctx) return;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, vw, vh);

        /*
         * pdfjs 6 : si `canvas` est omis, il défaut à `canvasContext.canvas` et active
         * InternalRenderTask.#canvasInUse — les rendus qui se chevauchent plantent alors
         * (« Cannot use the same canvas… ») et laissent la page blanche.
         * Forcer `canvas: null` + `canvasContext` contourne ce verrou.
         */
        const renderTask = page.render({ canvas: null, canvasContext: ctx, viewport });
        pdfActiveRenderTaskRef.current = renderTask;
        try {
          await renderTask.promise;
        } catch (e: unknown) {
          if (seq !== pdfRenderSeqRef.current) return;
          const name = e && typeof e === "object" && "name" in e ? String((e as { name?: string }).name) : "";
          if (name === "RenderingCancelledException" || name === "AbortException") return;
          throw e;
        } finally {
          if (pdfActiveRenderTaskRef.current === renderTask) {
            pdfActiveRenderTaskRef.current = null;
          }
        }
        if (seq !== pdfRenderSeqRef.current) return;

        await disposeFabric();
        if (seq !== pdfRenderSeqRef.current) return;
        if (!fabricHostRef.current) return;

        const fabricEl = document.createElement("canvas");
        fabricEl.width = vw;
        fabricEl.height = vh;
        fabricHost.replaceChildren(fabricEl);

        const canvas = new fabric.Canvas(fabricEl, {
          width: vw,
          height: vh,
          selection: true,
          /* v7 défaut true ; conserver l’ordre z des annotations à la sélection. */
          preserveObjectStacking: true,
          renderOnAddRemove: true,
        });
        canvas.backgroundColor = "";
        fabricRef.current = canvas;
        if (typeof window !== "undefined") {
          (window as Window & { __U3140_FABRIC_CANVAS__?: fabric.Canvas }).__U3140_FABRIC_CANVAS__ = canvas;
        }
        /* Fabric enveloppe le canvas : on positionne le wrapper au-dessus du PDF. */
        const fabricWrapper = fabricEl.parentElement ?? fabricHost;
        fabricWrapper.style.position = "absolute";
        fabricWrapper.style.left = "0";
        fabricWrapper.style.top = "0";
        fabricWrapper.style.width = `${vw}px`;
        fabricWrapper.style.height = `${vh}px`;
        fabricWrapper.style.zIndex = "10";
        fabricWrapper.style.pointerEvents = "auto";
        fabricWrapper.style.background = "transparent";
        fabricHost.style.position = "absolute";
        fabricHost.style.left = "0";
        fabricHost.style.top = "0";
        fabricHost.style.width = `${vw}px`;
        fabricHost.style.height = `${vh}px`;
        fabricHost.style.zIndex = "10";
        fabricHost.style.pointerEvents = "auto";
        fabricHost.style.background = "transparent";

        suppressAnnotatedDirtyRef.current = true;
        try {
          applyShapesToCanvas(canvas, shapesArg, pageNumber - 1, vw, vh);
        } finally {
          queueMicrotask(() => {
            suppressAnnotatedDirtyRef.current = false;
          });
        }

        const sync = () => {
          mergeShapesForPage(pageNumber - 1, vw, vh, canvas);
          if (!suppressAnnotatedDirtyRef.current) {
            u3142MarkAnnotatedLotEditedForPage0(
              lotChainRef.current,
              pageNumber - 1,
              annotatedEditedLotDetailIdsRef.current
            );
          }
        };
        const onObjectModified = (e: ModifiedEvent) => {
          const raw = e.target;
          let needRender = false;
          let needStyle = false;
          if (raw && u3142IsTextFrameGroup(raw as FabricObject)) {
            const grp = raw as fabric.Group;
            if (u3142NormalizeTextFrameGroupAfterTransform(grp)) {
              needRender = true;
              needStyle = true;
            }
            u3142SyncTextFrameBorderRect(grp);
          } else if (raw instanceof fabric.Textbox && raw.group && u3142IsTextFrameGroup(raw.group)) {
            if (u3142NormalizeFabricTextBoxAfterTransform(raw)) {
              needRender = true;
              needStyle = true;
            }
            u3142SyncTextFrameBorderRect(raw.group);
          } else if (raw && isArrowFabricGroup(raw)) {
            if (u3142NormalizeArrowFabricGroupAfterTransform(canvas, raw as fabric.Group)) {
              needRender = true;
              needStyle = true;
            }
          } else {
            const target = raw ? getFabricTargetForStyle(raw) : null;
            if (target instanceof fabric.Textbox && u3142NormalizeFabricTextBoxAfterTransform(target)) {
              if (target.group && u3142IsTextFrameGroup(target.group)) {
                u3142SyncTextFrameBorderRect(target.group);
              }
              needRender = true;
              needStyle = true;
            }
          }
          if (needRender) {
            canvas.requestRenderAll();
          }
          if (needStyle) {
            syncStyleUIFromFabricSelectionRef.current();
          }
          sync();
        };
        canvas.on("object:modified", onObjectModified);
        canvas.on("object:added", sync);
        canvas.on("object:removed", sync);
        canvas.on("text:changed", (e: { target: fabric.IText }) => {
          const tgt = e.target;
          if (tgt instanceof fabric.Textbox) {
            u3142RefreshPdfTextboxLayout(tgt);
            if (tgt.group && u3142IsTextFrameGroup(tgt.group)) {
              u3142SyncTextFrameBorderRect(tgt.group);
            }
          }
          sync();
        });
        canvas.on("mouse:over", (e: TPointerEventInfo) => {
          const target = e.target;
          if (!target) {
            return;
          }
          const metadata = u3142ReadAnnotationMetadataFromFabricObject(target);
          if (metadata.createdBy || metadata.createdByUser || metadata.createdAt) {
            setHoveredAnnotationMeta(metadata);
          } else {
            setHoveredAnnotationMeta(null);
          }
        });
        canvas.on("mouse:out", () => {
          setHoveredAnnotationMeta(null);
        });

        const pushSel = () => syncStyleUIFromFabricSelectionRef.current();
        canvas.on("selection:created", () => {
          setHasFabricSelection(true);
          u3142SyncTextFrameEditableFromSelection(canvas);
          pushSel();
        });
        canvas.on("selection:updated", () => {
          setHasFabricSelection(true);
          u3142SyncTextFrameEditableFromSelection(canvas);
          pushSel();
        });
        canvas.on("text:editing:exited", (e: { target: fabric.IText }) => {
          const t = e.target;
          if (t instanceof fabric.Textbox && t.group && u3142IsTextFrameGroup(t.group)) {
            u3142RefreshPdfTextboxLayout(t);
            u3142SyncTextFrameBorderRect(t.group);
            canvas.requestRenderAll();
            sync();
          }
          pushSel();
        });
        canvas.on("selection:cleared", () => {
          setHasFabricSelection(false);
          setAnnotationSelectionKind("none");
          u3142SyncTextFrameEditableFromSelection(canvas);
          setHoveredAnnotationMeta(null);
        });

        const handleCanvasMouseDown = (opt: TPointerEventInfo) => {
          const pl = placementRef.current;
          if (pl.type === "none") return;
          if (opt.target) return;
          const pointer = opt.scenePoint;
          if (!pointer) return;

        const placementKind: U3140PdfAnnotationShapeKind =
          pl.type === "rect"
            ? "rect"
            : pl.type === "text"
              ? "text"
              : pl.type === "line"
                ? "line"
                : "arrow";
        const sty = defaultsByKindRef.current[placementKind];

        const pageIndex0 = pageNumber - 1;
        const { vw, vh } = viewportSizeRef.current;
        if (vw <= 0 || vh <= 0) return;

        if (pl.type === "rect") {
          const rectSizeSty = u3140RectStyleForPlacement(
            appliedToolbarPresetIdRef.current,
            toolbarPresetsRef.current,
            sty
          );
          const { w, h } = u3140ResolveRectCanvasSize(vw, vh, rectSizeSty);
          let left = pointer.x;
          let top = pointer.y;
          left = Math.max(0, Math.min(left, vw - w));
          top = Math.max(0, Math.min(top, vh - h));
          const fillTransparent = sty.fillColor === U3140_PDF_ANNOTATION_FILL_NONE;
          const r = new fabric.Rect({
            left,
            top,
            originX: "left",
            originY: "top",
            width: w,
            height: h,
            fill: fillTransparent ? "rgba(0,0,0,0)" : sty.fillColor,
            opacity: sty.opacity,
            stroke: sty.strokeWidth > 0 ? sty.strokeColor : undefined,
            strokeWidth: sty.strokeWidth > 0 ? sty.strokeWidth : 0,
            selectable: true,
          });
          fabricObjectId(r);
          canvas.add(r);
          canvas.setActiveObject(r);
          goPlacementRef.current(u3142NextPlacementAfterInsert(pl));
          mergeShapesForPage(pageIndex0, vw, vh, canvas);
          canvas.requestRenderAll();
          return;
        }

        if (pl.type === "line") {
          if (!pl.lineStart) {
            goPlacementRef.current({ type: "line", lineStart: { x: pointer.x, y: pointer.y } });
            return;
          }
          const x1 = pl.lineStart.x;
          const y1 = pl.lineStart.y;
          const x2 = pointer.x;
          const y2 = pointer.y;
          const ln = new fabric.Line([x1, y1, x2, y2], {
            stroke: sty.strokeColor,
            strokeWidth: Math.max(1, sty.strokeWidth),
            opacity: sty.opacity,
            selectable: true,
          });
          fabricObjectId(ln);
          canvas.add(ln);
          u3142NudgeFabricLineToCanvasEndpoints(ln, x1, y1, x2, y2);
          u3142ConfigureLineLikeControls(ln);
          canvas.setActiveObject(ln);
          goPlacementRef.current(u3142NextPlacementAfterInsert(pl));
          mergeShapesForPage(pageIndex0, vw, vh, canvas);
          canvas.requestRenderAll();
          return;
        }

        if (pl.type === "arrow") {
          if (sty.strokeWidth <= 0) {
            return;
          }
          if (!pl.lineStart) {
            goPlacementRef.current({ type: "arrow", lineStart: { x: pointer.x, y: pointer.y } });
            return;
          }
          const x1 = pl.lineStart.x;
          const y1 = pl.lineStart.y;
          const x2 = pointer.x;
          const y2 = pointer.y;
          const grp = createArrowFabricGroup(x1, y1, x2, y2, sty.strokeColor, Math.max(1, sty.strokeWidth));
          grp.set({ opacity: sty.opacity });
          canvas.add(grp);
          canvas.setActiveObject(grp);
          goPlacementRef.current(u3142NextPlacementAfterInsert(pl));
          mergeShapesForPage(pageIndex0, vw, vh, canvas);
          canvas.requestRenderAll();
          return;
        }

        if (pl.type === "text") {
          const label = tRef.current("dlgPdfAnnotationDefaultText");
          const textFill = sty.textColor || "#111827";
          const bgTransparent =
            sty.fillColor === U3140_PDF_ANNOTATION_FILL_NONE || !sty.fillColor;
          const left = Math.max(0, Math.min(pointer.x, vw - 40));
          const topCorner = Math.max(24, Math.min(pointer.y, vh - 8));
          let boxW = Math.max(48, vw * (sty.textBoxWidthNorm ?? U3142_PDF_TEXT_BOX_WIDTH_DEFAULT_NORM));
          boxW = Math.min(boxW, Math.max(48, vw - left));
          const grp = createU3142TextFrameGroup({
            text: label,
            left,
            top: topCorner,
            originY: "top",
            width: boxW,
            fontSize: Math.round(Number(sty.fontSizePts)) || 14,
            fill: textFill,
            backgroundColor: bgTransparent ? "" : sty.fillColor,
            padding: bgTransparent ? 0 : 4,
            strokeColor: sty.strokeColor,
            strokeWidth: sty.strokeWidth,
            opacity: sty.opacity,
            textAlign: sty.textAlign ?? "left",
            textBoxVerticalAlign: u3142NormalizeTextBoxVerticalAlign(sty.textBoxVerticalAlign ?? "top"),
          });
          canvas.add(grp);
          const inner = u3142GetTextFrameMembers(grp)?.text;
          if (inner) {
            applyITextGlyphColor(canvas, inner, textFill);
            u3142RefreshPdfTextboxLayout(inner);
            u3142SyncTextFrameBorderRect(grp);
          }
          canvas.setActiveObject(grp);
          goPlacementRef.current(u3142NextPlacementAfterInsert(pl));
          mergeShapesForPage(pageIndex0, vw, vh, canvas);
          canvas.requestRenderAll();
          /* Après le cycle mousedown/mouseup : ré-entrée + focus (Fabric 7 / navigateur). */
          window.setTimeout(() => {
            if (canvas.getActiveObject() === grp) {
              u3142EnterTextFrameEditing(canvas, grp);
            }
          }, 0);
        }
      };

      canvas.on("mouse:down", handleCanvasMouseDown);
      canvas.on("mouse:up", (opt: TPointerEventInfo & { isClick?: boolean }) => {
        if (placementRef.current.type !== "none") return;
        if (!opt.isClick) return;
        const active = canvas.getActiveObject();
        if (!u3142IsTextFrameGroup(active)) return;
        const m = u3142GetTextFrameMembers(active);
        if (!m) return;
        if (!m.text.isEditing) {
          u3142EnterTextFrameEditing(canvas, active);
        } else {
          m.text.hiddenTextarea?.focus();
        }
      });
      canvas.on("mouse:dblclick", (opt: TPointerEventInfo) => {
        const target = opt.target;
        const frame = u3142IsTextFrameGroup(target)
          ? target
          : target instanceof fabric.Textbox && target.group && u3142IsTextFrameGroup(target.group)
            ? target.group
            : null;
        if (!frame) return;
        canvas.setActiveObject(frame);
        u3142EnterTextFrameEditing(canvas, frame);
      });
      } catch (err) {
        console.error("[U3140] renderPdfPage failed", err);
        if (seq === pdfRenderSeqRef.current) {
          await disposeFabric();
        }
      }
      };

      const next = pdfRenderChainRef.current.then(run, run);
      pdfRenderChainRef.current = next.then(
        () => undefined,
        () => undefined
      );
      await next;
    },
    [disposeFabric, mergeShapesForPage]
  );

  const performUndo = useCallback(() => {
    if (loading || applyingHistoryRef.current) return;
    const stack = undoStackRef.current;
    if (stack.length === 0) return;
    if (historyTimerRef.current != null) {
      window.clearTimeout(historyTimerRef.current);
      historyTimerRef.current = null;
    }
    historyIdleRef.current = true;
    historyBatchBeforeRef.current = null;

    const shapesBeforeUndo = u3142CloneShapes(shapesRef.current);
    const prev = stack.pop()!;
    redoStackRef.current.push(u3142CloneShapes(shapesRef.current));
    applyingHistoryRef.current = true;
    shapesRef.current = u3142CloneShapes(prev);
    u3142MarkAnnotatedLotsFromShapesDiff(
      lotChainRef.current,
      shapesBeforeUndo,
      shapesRef.current,
      annotatedEditedLotDetailIdsRef.current
    );
    void renderPdfPage(pageNum, shapesRef.current).finally(() => {
      applyingHistoryRef.current = false;
      fabricRef.current?.discardActiveObject();
      setAnnotationSelectionKind("none");
      setHasFabricSelection(false);
      setHistoryStackSizes({
        undo: undoStackRef.current.length,
        redo: redoStackRef.current.length,
      });
    });
  }, [loading, pageNum, renderPdfPage]);

  const performRedo = useCallback(() => {
    if (loading || applyingHistoryRef.current) return;
    const stack = redoStackRef.current;
    if (stack.length === 0) return;
    if (historyTimerRef.current != null) {
      window.clearTimeout(historyTimerRef.current);
      historyTimerRef.current = null;
    }
    historyIdleRef.current = true;
    historyBatchBeforeRef.current = null;

    const shapesBeforeRedo = u3142CloneShapes(shapesRef.current);
    const next = stack.pop()!;
    undoStackRef.current.push(u3142CloneShapes(shapesRef.current));
    applyingHistoryRef.current = true;
    shapesRef.current = u3142CloneShapes(next);
    u3142MarkAnnotatedLotsFromShapesDiff(
      lotChainRef.current,
      shapesBeforeRedo,
      shapesRef.current,
      annotatedEditedLotDetailIdsRef.current
    );
    void renderPdfPage(pageNum, shapesRef.current).finally(() => {
      applyingHistoryRef.current = false;
      fabricRef.current?.discardActiveObject();
      setAnnotationSelectionKind("none");
      setHasFabricSelection(false);
      setHistoryStackSizes({
        undo: undoStackRef.current.length,
        redo: redoStackRef.current.length,
      });
    });
  }, [loading, pageNum, renderPdfPage]);

  const deleteSelection = useCallback(() => {
    const canvas = fabricRef.current;
    const { vw, vh } = viewportSizeRef.current;
    if (!canvas) return;
    const active = canvas.getActiveObjects();
    if (!active.length) return;
    const toRemove = new Set<FabricObject>();
    active.forEach((o: FabricObject) => {
      if (o instanceof fabric.Textbox && o.group && u3142IsTextFrameGroup(o.group)) {
        toRemove.add(o.group);
      } else {
        toRemove.add(o);
      }
    });
    toRemove.forEach((o) => canvas.remove(o));
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    if (vw > 0 && vh > 0) {
      mergeShapesForPage(pageNum - 1, vw, vh, canvas);
    }
    setAnnotationSelectionKind("none");
    setHasFabricSelection(false);
  }, [mergeShapesForPage, pageNum]);
  deleteSelectionRef.current = deleteSelection;

  useEffect(() => {
    if (!isOpen || !documentId) return;

    let cancelled = false;
    (async () => {
      goPlacement({ type: "none" });
      setAnnotationSelectionKind("none");
      setDiscardConfirmOpen(false);
      setHasFabricSelection(false);
      setDefaultsByKind(u3142InitialDefaultsByKind());
      setToolbarPresets([]);
      setAppliedToolbarPresetId(null);
      setPresetCreateOpen(false);
      setPresetLabelDraft("");
      setPresetHintDraft("");
      setPresetDialogMode("edit");
      setPresetEditId(null);
      setCreateSourcePresetId(null);
      setPresetDeleteConfirmOpen(false);
      setLoading(true);
      setErrorMsg(null);
      pdfRenderChainRef.current = Promise.resolve();
      await disposeFabric();
      pdfDocRef.current = null;
      shapesRef.current = [];
      lotChainRef.current = [];
      baselineLotShapesJsonRef.current = new Map();
      annotatedEditedLotDetailIdsRef.current.clear();
      setHoveredAnnotationMeta(null);
      savedBaselineJsonRef.current = JSON.stringify([]);
      undoStackRef.current = [];
      redoStackRef.current = [];
      setPageNum(1);
      setNumPages(0);
      setPageSegments([]);
      try {
        const presets = await loadPresets();
        if (cancelled) return;
        setToolbarPresets(presets);

        const ann = await loadAnnotations(documentId);
        if (cancelled) return;
        const lotPayloads = ann.lotPayloads ?? [];
        const segments = ann.pageSegments ?? [];
        lotChainRef.current = u3142BuildLotChain(lotPayloads, segments);
        const flatShapes = lotPayloads.flatMap((lp) => lp.shapes);
        const initialShapes = dropDegenerateLineLikeShapes(flatShapes);
        shapesRef.current = initialShapes;
        savedBaselineJsonRef.current = JSON.stringify(shapesRef.current);
        baselineLotShapesJsonRef.current = u3142BuildLotBaselineMap(lotChainRef.current, shapesRef.current);
        undoStackRef.current = [];
        redoStackRef.current = [];
        setHistoryStackSizes({ undo: 0, redo: 0 });
        setPageSegments(segments);

        /* Édition : fond PDF sans aplat (GET …/pdf-file?variant=original) ; formes via GET …/annotations (loadAnnotations ci-dessus). */
        const pdfBuf = await loadOriginalPdfBuffer(documentId);
        const pdf = await getDocument({ data: pdfBuf }).promise;
        if (cancelled) return;
        pdfDocRef.current = pdf;
        const np = pdf.numPages;
        setNumPages(np);
      } catch {
        if (!cancelled) setErrorMsg(t("dlgPdfAnnotationLoadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (historyTimerRef.current != null) {
        window.clearTimeout(historyTimerRef.current);
        historyTimerRef.current = null;
      }
      historyIdleRef.current = true;
      historyBatchBeforeRef.current = null;
      pdfActiveRenderTaskRef.current?.cancel();
      pdfActiveRenderTaskRef.current = null;
      pdfRenderSeqRef.current += 1;
      void disposeFabric();
      pdfDocRef.current = null;
    };
  }, [isOpen, documentId, goPlacement, loadAnnotations, loadPresets, loadOriginalPdfBuffer, t, disposeFabric]);

  useEffect(() => {
    setAppliedToolbarPresetId((prev) => {
      if (prev == null || prev === "") {
        return prev;
      }
      return toolbarPresets.some((p) => String(p.id) === prev) ? prev : null;
    });
  }, [toolbarPresets]);

  useEffect(() => {
    if (!isOpen || !documentId || !pdfDocRef.current || numPages === 0 || loading) return;
    /* Double rAF : après ouverture du dialogue, le flex n’a pas toujours la largeur finale au 1er frame — sinon zoom PDF faux jusqu’à un autre re-render (ex. clic « Même angle »). */
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) {
          void renderPdfPage(pageNumRef.current, shapesRef.current);
        }
      });
    });
    return () => {
      cancelled = true;
    };
  }, [pageNum, numPages, loading, isOpen, documentId, renderPdfPage]);

  useEffect(() => {
    if (!isOpen || !pdfScrollAreaRef.current) return;
    pdfScrollAreaWidthLastRenderRef.current = 0;
    const el = pdfScrollAreaRef.current;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      /* Ne pas bloquer sur `loading` : le 1er redimensionnement peut arriver pendant le chargement ; sinon on rate le layout et le scale reste faux. */
      if (!pdfDocRef.current || pdfDocRef.current.numPages < 1) return;
      const w = Math.round(el.clientWidth);
      if (Math.abs(w - pdfScrollAreaWidthLastRenderRef.current) <= PDF_SCROLL_AREA_WIDTH_RERENDER_EPSILON_PX) {
        return;
      }
      pdfScrollAreaWidthLastRenderRef.current = w;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        void renderPdfPage(pageNumRef.current, shapesRef.current);
      });
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [isOpen, renderPdfPage]);

  useEffect(() => {
    if (historyTimerRef.current != null) {
      window.clearTimeout(historyTimerRef.current);
      historyTimerRef.current = null;
    }
    historyIdleRef.current = true;
    historyBatchBeforeRef.current = null;
  }, [pageNum]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        if (loading || saving) return;
        e.preventDefault();
        if (e.shiftKey) {
          performRedo();
        } else {
          performUndo();
        }
        return;
      }
      if (e.key === "Escape" && placementRef.current.type !== "none") {
        goPlacementRef.current({ type: "none" });
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (loading || saving) return;
        const c = fabricRef.current;
        if (!c) return;
        const active = c.getActiveObject();
        if (active instanceof fabric.IText && active.isEditing) return;
        if (placementRef.current.type !== "none") return;
        if (c.getActiveObjects().length === 0) return;
        e.preventDefault();
        deleteSelectionRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, loading, saving, performRedo, performUndo]);

  /** Nouveau canvas (changement de page, rotation, re-render PDF…) : la sélection Fabric est perdue sans `selection:cleared`. */
  useEffect(() => {
    setAnnotationSelectionKind("none");
    setHasFabricSelection(false);
  }, [pageNum, numPages, loading, isOpen, documentId]);

  useEffect(() => {
    const c = fabricRef.current;
    if (!c) return;
    const placing = placement.type !== "none";
    c.defaultCursor = placing ? "crosshair" : "default";
    c.hoverCursor = placing ? "crosshair" : "move";
    c.requestRenderAll();
  }, [placement]);

  const performCloseInternal = useCallback(() => {
    if (historyTimerRef.current != null) {
      window.clearTimeout(historyTimerRef.current);
      historyTimerRef.current = null;
    }
    historyIdleRef.current = true;
    historyBatchBeforeRef.current = null;
    suppressHistoryRef.current = true;
    try {
      goPlacement({ type: "none" });
      setAnnotationSelectionKind("none");
      setHasFabricSelection(false);
      setHoveredAnnotationMeta(null);
      pdfActiveRenderTaskRef.current?.cancel();
      pdfActiveRenderTaskRef.current = null;
      pdfRenderSeqRef.current += 1;
      const canvas = fabricRef.current;
      const { vw, vh } = viewportSizeRef.current;
      if (canvas && vw > 0 && vh > 0) {
        mergeShapesForPage(pageNum - 1, vw, vh, canvas);
      }
      void disposeFabric();
      pdfDocRef.current = null;
      onClose();
    } finally {
      suppressHistoryRef.current = false;
    }
  }, [disposeFabric, goPlacement, mergeShapesForPage, onClose, pageNum]);

  const requestClose = useCallback(
    (reason: string) => {
      if (reason === "saved") {
        performCloseInternal();
        return;
      }
      try {
        if (savedBaselineJsonRef.current === JSON.stringify(shapesRef.current)) {
          performCloseInternal();
          return;
        }
      } catch {
        performCloseInternal();
        return;
      }
      setDiscardConfirmOpen(true);
    },
    [performCloseInternal]
  );

  const handleSave = async () => {
    if (!documentId) return;
    suppressHistoryRef.current = true;
    try {
      const canvas = fabricRef.current;
      const { vw, vh } = viewportSizeRef.current;
      if (canvas && vw > 0 && vh > 0) {
        mergeShapesForPage(pageNum - 1, vw, vh, canvas);
      }
      const lotPayloads = u3142BuildLotPayloadsForSave(lotChainRef.current, shapesRef.current);
      /* Garde-fou BU314212 : si le mapping shape→lot perd des annotations (multi-lots sans pageSegments, ou shape hors plage), on avorte plutôt que d'écraser silencieusement les données serveur. */
      const totalShapes = shapesRef.current.length;
      const totalMapped = u3142CountShapesInLotPayloads(lotPayloads);
      if (totalShapes > 0 && totalMapped < totalShapes) {
        console.warn(
          "[U3142] Sauvegarde annotations annulée : %d/%d shapes n'ont pas pu être mappées à un lot (chain=%d, hasRanges=%s).",
          totalMapped,
          totalShapes,
          lotChainRef.current.length,
          lotChainRef.current.every((e) => e.startPage0 != null && e.endPage0 != null)
        );
        toast.error(
          t("msgPdfAnnotationSaveLotMappingError", {mapped: totalMapped, total: totalShapes }),
          t("errorTitle")
        );
        return;
      }
      const lotPayloadsForPut = u3142ApplyAnnotatedFlagsForUserEditedLots(
        lotPayloads,
        baselineLotShapesJsonRef.current,
        annotatedEditedLotDetailIdsRef.current
      );
      const payload: U3140DocumentPdfAnnotationsPayload = {
        schemaVersion: 1,
        lotPayloads: lotPayloadsForPut,
      };
      setSaving(true);
      try {
        await saveAnnotations(documentId, payload);
        toast.success(t("msgPdfAnnotationSaved"), t("successTitle"));
        onSaved?.();
        savedBaselineJsonRef.current = JSON.stringify(shapesRef.current);
        baselineLotShapesJsonRef.current = u3142BuildLotBaselineMap(lotChainRef.current, shapesRef.current);
        annotatedEditedLotDetailIdsRef.current.clear();
        undoStackRef.current = [];
        redoStackRef.current = [];
        setHistoryStackSizes({ undo: 0, redo: 0 });
        /* Réinitialiser avant fermeture : sinon `saving` reste true si le composant reste monté (dialogue masqué) et les boutons restent désactivés au réouvertage. */
        setSaving(false);
        performCloseInternal();
      } catch {
        setSaving(false);
      }
    } finally {
      suppressHistoryRef.current = false;
    }
  };

  const handleInsertToolFromMenu = useCallback(
    (tool: U3140PdfAnnotationInsertToolId) => {
      if (loading) return;
      fabricRef.current?.discardActiveObject();
      setAnnotationSelectionKind("none");
      setHasFabricSelection(false);
      if (tool === "rect") goPlacement({ type: "rect" });
      else if (tool === "line") goPlacement({ type: "line", lineStart: null });
      else if (tool === "arrow") goPlacement({ type: "arrow", lineStart: null });
      else goPlacement({ type: "text" });
    },
    [loading, goPlacement]
  );

  const handlePickFillColor = useCallback(
    (hex: string) => {
      setFillColor(hex);
      const canvas = fabricRef.current;
      const active = canvas?.getActiveObject();
      const target = active ? getFabricTargetForStyle(active) : null;
      if (canvas && target && (target instanceof fabric.Rect || target instanceof fabric.IText || u3142IsTextFrameGroup(target))) {
        writeFillToFabricSelection(canvas, target, hex);
        syncStyleUIFromFabricSelectionRef.current();
        mergeActivePageShapesFromCanvas();
        if (target instanceof fabric.Rect) {
          setDefaultsByKind((prev) => ({ ...prev, rect: { ...prev.rect, fillColor: hex } }));
        } else {
          setDefaultsByKind((prev) => ({ ...prev, text: { ...prev.text, fillColor: hex } }));
        }
        return;
      }
      const k = getKindForSharedToolbarDefaults(canvas, placementRef.current) ?? "rect";
      setDefaultsByKind((prev) => ({ ...prev, [k]: { ...prev[k], fillColor: hex } }));
    },
    [mergeActivePageShapesFromCanvas]
  );

  const handleClearFillColor = useCallback(() => {
    setFillColor(U3140_PDF_ANNOTATION_FILL_NONE);
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    const target = active ? getFabricTargetForStyle(active) : null;
    if (canvas && target && (target instanceof fabric.Rect || target instanceof fabric.IText || u3142IsTextFrameGroup(target))) {
      writeFillToFabricSelection(canvas, target, U3140_PDF_ANNOTATION_FILL_NONE);
      syncStyleUIFromFabricSelectionRef.current();
      mergeActivePageShapesFromCanvas();
      if (target instanceof fabric.Rect) {
        setDefaultsByKind((prev) => ({
          ...prev,
          rect: { ...prev.rect, fillColor: U3140_PDF_ANNOTATION_FILL_NONE },
        }));
      } else {
        setDefaultsByKind((prev) => ({
          ...prev,
          text: { ...prev.text, fillColor: U3140_PDF_ANNOTATION_FILL_NONE },
        }));
      }
      return;
    }
    const k = getKindForSharedToolbarDefaults(canvas, placementRef.current) ?? "rect";
    setDefaultsByKind((prev) => ({
      ...prev,
      [k]: { ...prev[k], fillColor: U3140_PDF_ANNOTATION_FILL_NONE },
    }));
  }, [mergeActivePageShapesFromCanvas]);

  const handlePickTextColor = useCallback(
    (hex: string) => {
      setTextColor(hex);
      const canvas = fabricRef.current;
      const active = canvas?.getActiveObject();
      const target = active ? getFabricTargetForStyle(active) : null;
      const textLeaf =
        target && u3142IsTextFrameGroup(target)
          ? u3142GetTextFrameMembers(target)?.text
          : target instanceof fabric.IText
            ? target
            : null;
      if (canvas && textLeaf instanceof fabric.IText) {
        applyITextGlyphColor(canvas, textLeaf, hex);
        if (textLeaf instanceof fabric.Textbox) {
          u3142RefreshPdfTextboxLayout(textLeaf);
          if (textLeaf.group && u3142IsTextFrameGroup(textLeaf.group)) {
            u3142SyncTextFrameBorderRect(textLeaf.group);
          }
        }
        syncStyleUIFromFabricSelectionRef.current();
        mergeActivePageShapesFromCanvas();
        return;
      }
      const k = getKindForToolbarDefaultPersistence(canvas, placementRef.current);
      if (k === "text") {
        setDefaultsByKind((prev) => ({ ...prev, text: { ...prev.text, textColor: hex } }));
      }
    },
    [mergeActivePageShapesFromCanvas]
  );

  const handlePickStrokeColor = useCallback(
    (hex: string) => {
      const canvas = fabricRef.current;
      const active = canvas?.getActiveObject();
      const target = active ? getFabricTargetForStyle(active) : null;
      if (canvas && target) {
        writeStrokeToFabricSelection(canvas, target, hex);
        syncStyleUIFromFabricSelectionRef.current();
        mergeActivePageShapesFromCanvas();
        const kind = getKindFromFabricStyleTarget(target);
        if (kind) {
          setDefaultsByKind((prev) => ({
            ...prev,
            [kind]: {
              ...prev[kind],
              strokeColor: hex,
              strokeWidth: prev[kind].strokeWidth <= 0 ? 2 : prev[kind].strokeWidth,
            },
          }));
        }
        return;
      }
      setStrokeColor(hex);
      setStrokeWidth((w) => (w <= 0 ? 2 : w));
      const k = getKindForSharedToolbarDefaults(canvas, placementRef.current) ?? "rect";
      setDefaultsByKind((prev) => ({
        ...prev,
        [k]: { ...prev[k], strokeColor: hex, strokeWidth: prev[k].strokeWidth <= 0 ? 2 : prev[k].strokeWidth },
      }));
    },
    [mergeActivePageShapesFromCanvas]
  );

  const handleClearStrokeColor = useCallback(() => {
    const canvas = fabricRef.current;
    const active = canvas?.getActiveObject();
    const target = active ? getFabricTargetForStyle(active) : null;
    if (canvas && target) {
      if (target instanceof fabric.Rect) {
        target.set({ strokeWidth: 0, stroke: undefined });
      } else if (target instanceof fabric.Line) {
        target.set({ strokeWidth: 0 });
      } else if (u3142IsTextFrameGroup(target)) {
        const m = u3142GetTextFrameMembers(target);
        if (m) {
          m.border.set({ strokeWidth: 0, stroke: undefined });
          u3142SyncTextFrameBorderRect(target);
        }
      } else if (target instanceof fabric.IText) {
        if (target.group && u3142IsTextFrameGroup(target.group)) {
          const m = u3142GetTextFrameMembers(target.group);
          if (m) {
            m.border.set({ strokeWidth: 0, stroke: undefined });
            u3142SyncTextFrameBorderRect(target.group);
          }
        } else {
          target.set({ strokeWidth: 0, stroke: undefined });
        }
      } else if (isArrowFabricGroup(target)) {
        for (const ch of target.getObjects()) {
          if (ch instanceof fabric.Line) ch.set({ strokeWidth: 0 });
        }
      }
      setStrokeWidth(0);
      canvas.requestRenderAll();
      syncStyleUIFromFabricSelectionRef.current();
      mergeActivePageShapesFromCanvas();
      {
        const kind = getKindFromFabricStyleTarget(target);
        if (kind) {
          setDefaultsByKind((prev) => ({ ...prev, [kind]: { ...prev[kind], strokeWidth: 0 } }));
        }
      }
      return;
    }
    setStrokeWidth(0);
    const k = getKindForSharedToolbarDefaults(canvas, placementRef.current) ?? "rect";
    setDefaultsByKind((prev) => ({ ...prev, [k]: { ...prev[k], strokeWidth: 0 } }));
  }, [mergeActivePageShapesFromCanvas]);

  const handleOpacitySliderChange = useCallback(
    (value: number) => {
      setOpacity(value);
      const canvas = fabricRef.current;
      const active = canvas?.getActiveObject();
      const target = active ? getFabricTargetForStyle(active) : null;
      if (canvas && target) {
        writeOpacityToFabricSelection(canvas, target, value);
        syncStyleUIFromFabricSelectionRef.current();
        mergeActivePageShapesFromCanvas();
        const kind = getKindFromFabricStyleTarget(target);
        if (kind) {
          setDefaultsByKind((prev) => ({ ...prev, [kind]: { ...prev[kind], opacity: value } }));
        }
        return;
      }
      const k = getKindForSharedToolbarDefaults(canvas, placementRef.current) ?? "rect";
      setDefaultsByKind((prev) => ({ ...prev, [k]: { ...prev[k], opacity: value } }));
    },
    [mergeActivePageShapesFromCanvas]
  );

  const handleStrokeWidthInputChange = useCallback(
    (value: number) => {
      setStrokeWidth(value);
      const canvas = fabricRef.current;
      const active = canvas?.getActiveObject();
      const target = active ? getFabricTargetForStyle(active) : null;
      if (canvas && target) {
        const { vw, vh } = viewportSizeRef.current;
        if (isArrowFabricGroup(target) && vw > 0 && vh > 0) {
          writeStrokeWidthToFabricSelection(canvas, target, value, {
            vw,
            vh,
            pageIndex0: pageNum - 1,
          });
        } else {
          writeStrokeWidthToFabricSelection(canvas, target, value);
        }
        syncStyleUIFromFabricSelectionRef.current();
        mergeActivePageShapesFromCanvas();
        const kind = getKindFromFabricStyleTarget(target);
        if (kind) {
          setDefaultsByKind((prev) => ({ ...prev, [kind]: { ...prev[kind], strokeWidth: value } }));
        }
        return;
      }
      const k = getKindForSharedToolbarDefaults(canvas, placementRef.current) ?? "rect";
      setDefaultsByKind((prev) => ({ ...prev, [k]: { ...prev[k], strokeWidth: value } }));
    },
    [mergeActivePageShapesFromCanvas, pageNum]
  );

  const applyPdfAnnotationToolbarPresetStyles = useCallback(
    (
      styles: U3140PdfAnnotationToolbarPresetStyles,
      options?: { forceToolbarKind?: U3140PdfAnnotationShapeKind }
    ) => {
      if (loading) {
        return;
      }
      const merged = u3140MergeToolbarPresetStyles(styles);
      const next = merged as unknown as Record<U3140PdfAnnotationShapeKind, U3140PdfAnnotationToolbarStyle>;
      setDefaultsByKind(next);
      defaultsByKindRef.current = next;

      const canvas = fabricRef.current;
      const pl = placementRef.current;
      const fp = options?.forceToolbarKind
        ? options.forceToolbarKind
        : getKindForToolbarDefaultPersistence(canvas ?? undefined, pl);
      const plKind = (): U3140PdfAnnotationShapeKind | null => {
        if (pl.type === "none") return null;
        if (pl.type === "rect") return "rect";
        if (pl.type === "text") return "text";
        if (pl.type === "line") return "line";
        return "arrow";
      };
      const sliceKind: U3140PdfAnnotationShapeKind =
        options?.forceToolbarKind ??
        getKindForSharedToolbarDefaults(canvas ?? undefined, pl) ??
        plKind() ??
        "rect";

      const sty = next[sliceKind];
      setFillColor(sty.fillColor);
      setStrokeColor(sty.strokeColor);
      setStrokeWidth(sty.strokeWidth);
      setOpacity(sty.opacity);
      if (sliceKind === "text") {
        setTextColor(sty.textColor);
        setFontSizePts(sty.fontSizePts);
        setTextAlign(sty.textAlign);
        setTextBoxWidthNorm(sty.textBoxWidthNorm);
        setTextBoxVerticalAlign(sty.textBoxVerticalAlign);
      }

      if (canvas && (fp === "rect" || fp === "line" || fp === "arrow")) {
        const active = canvas.getActiveObject();
        const target = active ? getFabricTargetForStyle(active) : null;
        if (target) {
          const s = next[fp];
          if (fp === "rect" && target instanceof fabric.Rect) {
            writeFillToFabricSelection(canvas, target, s.fillColor);
            target.set({ strokeWidth: s.strokeWidth, stroke: s.strokeWidth > 0 ? s.strokeColor : undefined });
            writeOpacityToFabricSelection(canvas, target, s.opacity);
          } else if (fp === "line" && target instanceof fabric.Line) {
            writeStrokeWidthToFabricSelection(canvas, target, s.strokeWidth);
            if (s.strokeWidth > 0) {
              writeStrokeToFabricSelection(canvas, target, s.strokeColor);
            }
            writeOpacityToFabricSelection(canvas, target, s.opacity);
          } else if (fp === "arrow" && isArrowFabricGroup(target)) {
            const { vw, vh } = viewportSizeRef.current;
            if (vw > 0 && vh > 0) {
              writeStrokeWidthToFabricSelection(canvas, target, s.strokeWidth, {
                vw,
                vh,
                pageIndex0: pageNum - 1,
              });
            } else {
              writeStrokeWidthToFabricSelection(canvas, target, s.strokeWidth);
            }
            if (s.strokeWidth > 0) {
              writeStrokeToFabricSelection(canvas, target, s.strokeColor);
            }
            writeOpacityToFabricSelection(canvas, target, s.opacity);
          }
          syncStyleUIFromFabricSelectionRef.current();
          mergeActivePageShapesFromCanvas();
        }
      }

      if (canvas && fp === "text") {
        const active = canvas.getActiveObject();
        const target = active ? getFabricTargetForStyle(active) : null;
        if (target && (u3142IsTextFrameGroup(target) || target instanceof fabric.IText)) {
          const s = next.text;
          writeFillToFabricSelection(canvas, target, s.fillColor);
          writeStrokeWidthToFabricSelection(canvas, target, s.strokeWidth);
          if (s.strokeWidth > 0) {
            writeStrokeToFabricSelection(canvas, target, s.strokeColor);
          }
          writeOpacityToFabricSelection(canvas, target, s.opacity);
          writeFontSizeToFabricSelection(canvas, target, Number(s.fontSizePts) || 14);
          const textLeaf =
            u3142IsTextFrameGroup(target)
              ? u3142GetTextFrameMembers(target)?.text
              : target instanceof fabric.IText
                ? target
                : null;
          if (textLeaf instanceof fabric.IText) {
            applyITextGlyphColor(canvas, textLeaf, s.textColor);
            textLeaf.set({ textAlign: (s.textAlign as "left" | "center" | "right") ?? "left" });
            if (textLeaf instanceof fabric.Textbox) {
              const aug = textLeaf as U3142PdfTextboxAug;
              aug._u3142TextBoxVerticalAlign = u3142NormalizeTextBoxVerticalAlign(s.textBoxVerticalAlign ?? "top");
              const { vw } = viewportSizeRef.current;
              if (vw > 0) {
                const br = textLeaf.getBoundingRect();
                const left = br.left;
                const wNorm = u3142ClampTextBoxWidthNorm(
                  Number(s.textBoxWidthNorm) || U3142_PDF_TEXT_BOX_WIDTH_DEFAULT_NORM
                );
                const boxW = Math.min(Math.max(0, vw - left), Math.max(48, wNorm * vw));
                textLeaf.set({ width: boxW });
              }
              u3142RefreshPdfTextboxLayout(textLeaf);
            }
            if (textLeaf.group && u3142IsTextFrameGroup(textLeaf.group)) {
              u3142SyncTextFrameBorderRect(textLeaf.group);
            }
            textLeaf.setCoords();
          }
          canvas.requestRenderAll();
          syncStyleUIFromFabricSelectionRef.current();
          mergeActivePageShapesFromCanvas();
        }
      }
    },
    [loading, mergeActivePageShapesFromCanvas, pageNum]
  );

  const handleOpenCreateToolbarPresetDialog = useCallback(() => {
    if (loading || saving) {
      return;
    }
    setPresetDialogMode(hasExistingPresets ? "edit" : "create");
    setPresetEditId(null);
    setCreateSourcePresetId(null);
    setPresetRenameOpen(false);
    setPresetLabelDraft("");
    setPresetHintDraft("");
    setPresetStylesDraft(u3140MergeToolbarPresetStyles(undefined));
    setPresetCreateOpen(true);
    setPresetBaselineSession((n) => n + 1);
  }, [loading, saving, hasExistingPresets]);

  const handleResetPresetDialogState = useCallback(() => {
    setPresetDialogMode(hasExistingPresets ? "edit" : "create");
    setPresetEditId(null);
    setCreateSourcePresetId(null);
    setPresetRenameOpen(false);
    setPresetLabelDraft("");
    setPresetHintDraft("");
    setPresetStylesDraft(u3140MergeToolbarPresetStyles(undefined));
    setPresetDeleteConfirmOpen(false);
  }, [hasExistingPresets]);

  useEffect(() => {
    if (!presetCreateOpen) return;
    if (hasExistingPresets) return;
    if (presetDialogMode === "create") return;
    setPresetDialogMode("create");
    setPresetEditId(null);
    setCreateSourcePresetId(null);
    setPresetRenameOpen(false);
    setPresetHintDraft("");
    setPresetStylesDraft(u3140MergeToolbarPresetStyles(undefined));
  }, [presetCreateOpen, hasExistingPresets, presetDialogMode]);

  useEffect(() => {
    if (!presetCreateOpen) {
      presetDialogBaselineRef.current = null;
      return;
    }
    presetDialogBaselineRef.current = {
      presetDialogMode,
      presetEditId,
      createSourcePresetId,
      presetLabelDraft,
      presetHintDraft,
      presetStylesDraft: u3140MergeToolbarPresetStyles(presetStylesDraft),
      presetRenameOpen,
    };
    // Baseline uniquement à l’ouverture (nouvelle session), pas à chaque frappe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetCreateOpen, presetBaselineSession]);

  const handleClosePresetDialog = useCallback(() => {
    if (presetSaving) {
      return;
    }
    setPresetCreateOpen(false);
    setPresetDialogMode("edit");
    setPresetEditId(null);
    setCreateSourcePresetId(null);
    setPresetRenameOpen(false);
    setPresetDeleteConfirmOpen(false);
  }, [presetSaving]);

  const handleCancelPresetDialog = useCallback(() => {
    if (presetSaving) {
      return;
    }
    const b = presetDialogBaselineRef.current;
    if (b) {
      setPresetDialogMode(b.presetDialogMode);
      setPresetEditId(b.presetEditId);
      setCreateSourcePresetId(b.createSourcePresetId);
      setPresetLabelDraft(b.presetLabelDraft);
      setPresetHintDraft(b.presetHintDraft);
      setPresetStylesDraft(u3140MergeToolbarPresetStyles(b.presetStylesDraft));
      setPresetRenameOpen(b.presetRenameOpen);
    }
    setPresetDeleteConfirmOpen(false);
  }, [presetSaving]);

  const handleSubmitToolbarPresetDialog = useCallback(async () => {
    if (!isPresetFormVisible) {
      return;
    }
    const label = presetLabelDraft.trim();
    if (!label) {
      toast.error(t("msgPdfAnnotationPresetLabelRequired"), t("errorTitle"));
      return;
    }
    if (presetDialogMode === "edit" && (presetEditId == null || presetEditId === "")) {
      toast.error(t("msgPdfAnnotationSelectPresetToEdit"), t("errorTitle"));
      return;
    }
    setPresetSaving(true);
    try {
      const styles = u3140MaterializeRectPresetStylesForSave(
        u3140MergeToolbarPresetStyles(presetStylesDraft)
      );
      const hintTrim = presetHintDraft.trim();
      const savedPresetIdBefore = presetEditId;
      if (presetDialogMode === "edit") {
        await updatePreset(presetEditId!, { label, styles, hint: hintTrim });
        toast.success(t("msgPdfAnnotationPresetUpdated"), t("successTitle"));
      } else {
        await createPreset({
          label,
          styles,
          ...(hintTrim !== "" ? { hint: hintTrim } : {}),
        });
        toast.success(t("msgPdfAnnotationPresetSaved"), t("successTitle"));
      }
      const refreshed = await loadPresets();
      setToolbarPresets(refreshed);
      const resolvedEditId =
        presetDialogMode === "edit" && savedPresetIdBefore != null
          ? String(savedPresetIdBefore)
          : refreshed.find((p) => p.label === label)?.id != null
            ? String(refreshed.find((p) => p.label === label)!.id)
            : presetEditId;
      if (presetDialogMode === "edit" && resolvedEditId) {
        setPresetEditId(resolvedEditId);
      }
      const savedFromList =
        resolvedEditId != null ? refreshed.find((p) => String(p.id) === String(resolvedEditId)) : undefined;
      const apiRect = savedFromList?.styles?.rect;
      const apiHasPx =
        apiRect != null &&
        Number.isFinite(apiRect.defaultWidthPx) &&
        Number.isFinite(apiRect.defaultHeightPx);
      const draftAfterSave = u3140MergeToolbarPresetStyles(apiHasPx ? savedFromList!.styles : styles);
      setPresetStylesDraft(draftAfterSave);
      const appliedId = appliedToolbarPresetIdRef.current;
      const targetId = resolvedEditId;
      if (appliedId && targetId && appliedId === targetId) {
        const appliedPreset = refreshed.find((p) => String(p.id) === appliedId);
        if (appliedPreset) {
          applyPdfAnnotationToolbarPresetStyles(appliedPreset.styles, { forceToolbarKind: "rect" });
        }
      }
      presetDialogBaselineRef.current = {
        presetDialogMode,
        presetEditId: resolvedEditId ?? presetEditId,
        createSourcePresetId,
        presetLabelDraft,
        presetHintDraft,
        presetStylesDraft: draftAfterSave,
        presetRenameOpen,
      };
    } finally {
      setPresetSaving(false);
    }
  }, [
    isPresetFormVisible,
    presetLabelDraft,
    presetHintDraft,
    presetDialogMode,
    presetEditId,
    createSourcePresetId,
    presetStylesDraft,
    presetRenameOpen,
    createPreset,
    updatePreset,
    loadPresets,
    applyPdfAnnotationToolbarPresetStyles,
    t,
    toast,
  ]);

  const handleDeleteSelectedPreset = useCallback(async () => {
    if (presetEditId == null || presetEditId === "") return;
    setPresetSaving(true);
    try {
      await deletePreset(presetEditId);
      const refreshed = await loadPresets();
      setToolbarPresets(refreshed);
      setPresetDeleteConfirmOpen(false);
      if (refreshed.length > 0) {
        const first = refreshed[0];
        const nextId = String(first.id);
        setPresetEditId(nextId);
        setPresetLabelDraft(first.label);
        setPresetHintDraft(first.hint ?? "");
        setPresetRenameOpen(false);
        setPresetStylesDraft(u3140MergeToolbarPresetStyles(first.styles));
        presetDialogBaselineRef.current = {
          presetDialogMode: "edit",
          presetEditId: nextId,
          createSourcePresetId,
          presetLabelDraft: first.label,
          presetHintDraft: first.hint ?? "",
          presetStylesDraft: u3140MergeToolbarPresetStyles(first.styles),
          presetRenameOpen: false,
        };
      } else {
        setPresetEditId(null);
        setPresetLabelDraft("");
        setPresetHintDraft("");
        setPresetRenameOpen(false);
        setPresetStylesDraft(u3140MergeToolbarPresetStyles(undefined));
        presetDialogBaselineRef.current = {
          presetDialogMode: "create",
          presetEditId: null,
          createSourcePresetId: null,
          presetLabelDraft: "",
          presetHintDraft: "",
          presetStylesDraft: u3140MergeToolbarPresetStyles(undefined),
          presetRenameOpen: false,
        };
      }
      toast.success(t("msgPdfAnnotationPresetDeleted"), t("successTitle"));
    } finally {
      setPresetSaving(false);
    }
  }, [presetEditId, createSourcePresetId, deletePreset, loadPresets, t, toast]);

  const handleApplyToolbarPreset = useCallback(
    (preset: U3140PdfAnnotationToolbarPreset) => {
      setAppliedToolbarPresetId(String(preset.id));
      const canvas = fabricRef.current;
      if (canvas) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
      applyPdfAnnotationToolbarPresetStyles(preset.styles, { forceToolbarKind: "rect" });
      goPlacement({ type: "rect" });
    },
    [applyPdfAnnotationToolbarPresetStyles, goPlacement]
  );

  const handleFontSizeInputChange = useCallback(
    (value: number) => {
      setFontSizePts(value);
      const canvas = fabricRef.current;
      const active = canvas?.getActiveObject();
      const target = active ? getFabricTargetForStyle(active) : null;
      if (canvas && target && (u3142IsTextFrameGroup(target) || target instanceof fabric.IText)) {
        writeFontSizeToFabricSelection(canvas, target, value);
        syncStyleUIFromFabricSelectionRef.current();
        mergeActivePageShapesFromCanvas();
        return;
      }
      const k = getKindForToolbarDefaultPersistence(canvas, placementRef.current);
      if (k === "text") {
        setDefaultsByKind((prev) => ({ ...prev, text: { ...prev.text, fontSizePts: value } }));
      }
    },
    [mergeActivePageShapesFromCanvas]
  );

  const handleTextAlignChange = useCallback(
    (align: U3140PdfAnnotationTextAlign) => {
      setTextAlign(align);
      const canvas = fabricRef.current;
      const active = canvas?.getActiveObject();
      const target = active ? getFabricTargetForStyle(active) : null;
      const textLeaf =
        target && u3142IsTextFrameGroup(target)
          ? u3142GetTextFrameMembers(target)?.text
          : target instanceof fabric.IText
            ? target
            : null;
      if (canvas && textLeaf instanceof fabric.IText) {
        textLeaf.set({ textAlign: align });
        textLeaf.setCoords();
        if (textLeaf.group && u3142IsTextFrameGroup(textLeaf.group)) {
          u3142SyncTextFrameBorderRect(textLeaf.group);
        }
        canvas.requestRenderAll();
        syncStyleUIFromFabricSelectionRef.current();
        mergeActivePageShapesFromCanvas();
        return;
      }
      const k = getKindForToolbarDefaultPersistence(canvas, placementRef.current);
      if (k === "text") {
        setDefaultsByKind((prev) => ({ ...prev, text: { ...prev.text, textAlign: align } }));
      }
    },
    [mergeActivePageShapesFromCanvas]
  );

  const handleTextBoxVerticalAlignChange = useCallback(
    (valign: U3140PdfAnnotationTextBoxVerticalAlign) => {
      setTextBoxVerticalAlign(valign);
      const canvas = fabricRef.current;
      const active = canvas?.getActiveObject();
      const target = active ? getFabricTargetForStyle(active) : null;
      const tb =
        target && u3142IsTextFrameGroup(target)
          ? u3142GetTextFrameMembers(target)?.text
          : target instanceof fabric.Textbox
            ? target
            : null;
      if (canvas && tb instanceof fabric.Textbox) {
        const aug = tb as U3142PdfTextboxAug;
        aug._u3142TextBoxVerticalAlign = valign;
        u3142RefreshPdfTextboxLayout(tb);
        if (tb.group && u3142IsTextFrameGroup(tb.group)) {
          u3142SyncTextFrameBorderRect(tb.group);
        }
        canvas.requestRenderAll();
        syncStyleUIFromFabricSelectionRef.current();
        mergeActivePageShapesFromCanvas();
        return;
      }
      const k = getKindForToolbarDefaultPersistence(canvas, placementRef.current);
      if (k === "text") {
        setDefaultsByKind((prev) => ({
          ...prev,
          text: { ...prev.text, textBoxVerticalAlign: valign },
        }));
      }
    },
    [mergeActivePageShapesFromCanvas]
  );

  const activeInsertTool = useMemo((): "none" | U3140PdfAnnotationInsertToolId => {
    if (placement.type === "none") return "none";
    if (placement.type === "rect") return "rect";
    if (placement.type === "text") return "text";
    if (placement.type === "line") return "line";
    return "arrow";
  }, [placement]);

  const shapesMenuLabels = useMemo(
    () => ({
      menuTitle: t("lblPdfAnnotationShapesInsert"),
      rect: t("btnPdfAnnotationAddRect"),
      line: t("btnPdfAnnotationAddLine"),
      arrow: t("btnPdfAnnotationAddArrow"),
      text: t("btnPdfAnnotationAddText"),
      clearPlacement: t("lblPdfAnnotationClearPlacement"),
    }),
    [t]
  );

  /** Des préréglages existent : il faut en appliquer un avant d’insérer une forme. */
  const shapesInsertBlockedNoPreset =
    toolbarPresets.length > 0 &&
    (appliedToolbarPresetId == null || appliedToolbarPresetId === "");
  const shapesMenuDisabled = loading || saving || presetSaving || shapesInsertBlockedNoPreset;
  const shapesMenuButtonTitle = shapesInsertBlockedNoPreset ? t("ttlPdfAnnotationShapesInsertRequiresPreset") : undefined;

  useEffect(() => {
    if (!shapesInsertBlockedNoPreset) return;
    if (placement.type === "none") return;
    goPlacement({ type: "none" });
  }, [shapesInsertBlockedNoPreset, placement.type, goPlacement]);

  const fillSwatchesDisabled =
    annotationSelectionKind === "line" ||
    annotationSelectionKind === "arrow" ||
    placement.type === "line" ||
    placement.type === "arrow";
  const strokeToolbarDisabled = shapesMenuDisabled;
  const strokeWidthToolbarDisabled = shapesMenuDisabled;
  const textColorToolbarVisible = annotationSelectionKind === "text" || placement.type === "text";

  const canUndo = historyStackSizes.undo > 0;
  const canRedo = historyStackSizes.redo > 0;

  const canGoPdfPagePrev = numPages > 0 && pageNum > 1;
  const canGoPdfPageNext = numPages > 0 && pageNum < numPages;
  const hoveredAnnotationCreatedBy =
    hoveredAnnotationMeta?.createdByUser ??
    hoveredAnnotationMeta?.createdBy ??
    t("lblPdfAnnotationUnknownUser");
  const hoveredAnnotationCreatedAt = useMemo(() => {
    if (!hoveredAnnotationMeta?.createdAt) {
      return t("lblPdfAnnotationUnknownDate");
    }
    const d = new Date(hoveredAnnotationMeta.createdAt);
    if (Number.isNaN(d.getTime())) {
      return hoveredAnnotationMeta.createdAt;
    }
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(d);
  }, [hoveredAnnotationMeta?.createdAt, t]);

  /** Même hauteur que le bouton du `U2000ComboBox` (`U2000ComboBoxBase` : `h-[46px]`), sans le label. */
  const u3142PdfPageNavBtnClassName =
    "h-[46px]! min-h-[46px]! max-h-[46px]! py-0! px-3! [&_svg]:mr-1!";

  if (!isOpen || !documentId) return null;

  return (
    <>
      <U2000BasicDialog
        isOpen={isOpen}
        onClose={requestClose}
        onBackdropClick={() => requestClose("backdrop")}
        title={t("dlgPdfAnnotationTitle")}
        width="w-[min(99vw,2560px)] max-w-[min(99vw,2560px)]"
        height="h-[min(98vh,1600px)] max-h-[98vh]"
        style={{ maxWidth: "min(99vw, 2560px)", width: "min(99vw, 2560px)", height: "min(98vh, 1600px)" }}
        contentOverflowVisible
        overlayClassName={[overlayClassName ?? "z-50", "p-2"].filter(Boolean).join(" ")}
      >
      <div className="flex min-h-0 flex-1 flex-col gap-2 px-1 pb-1">
        {errorMsg ? <p className="text-sm text-red-600">{errorMsg}</p> : null}
        {loading ? <p className="text-sm text-gray-600">{t("dlgPdfAnnotationLoading")}</p> : null}
        <div className="mb-2 flex min-h-[40px] w-full shrink-0 flex-wrap items-end gap-3 rounded-lg border border-gray-300 bg-muted/50 px-3 py-2 text-sm">
          {pageSegments.length > 0 ? (
            <div className="min-w-0 flex-[1_1_14rem]">
              <U2000ComboBox<U3142PdfToolbarComboItem>
                formStateHandlers={pdfSegmentComboHandlers}
                fieldName="pdfSegment"
                label={t("lblPdfAnnotationGoToSegment")}
                data={pdfSegmentComboData}
                keyField="key"
                descField="label"
                getValue={(item) => item.key}
                findItemByValue={(value, data) => data.find((d) => String(d.key) === String(value)) ?? null}
                formatLabel={(item) => String(item.label)}
                disabled={loading || numPages === 0}
                placeholder={t("optPdfAnnotationPickSegment")}
                maxHeight={280}
              />
            </div>
          ) : null}
          <div className="flex min-w-0 flex-[1_1_14rem] items-end gap-1">
            <div className="min-w-0 flex-1">
              <U2000ComboBox<U3142PdfToolbarComboItem>
                formStateHandlers={pdfPageComboHandlers}
                fieldName="pdfPage"
                label={t("lblPdfAnnotationPage")}
                data={pdfPageComboData}
                keyField="key"
                descField="label"
                getValue={(item) => item.key}
                findItemByValue={(value, data) => data.find((d) => String(d.key) === String(value)) ?? null}
                formatLabel={(item) => String(item.label)}
                disabled={loading || numPages === 0}
                maxHeight={280}
              />
            </div>
            <div className="flex shrink-0 flex-wrap items-end gap-1" role="group" aria-label={t("lblPdfAnnotationPageNavGroup")}>
              <U2000ActionBtn
                variant="secondary"
                icon={FaChevronLeft}
                label={<span className="sr-only">{t("btnPdfAnnotationPagePrev")}</span>}
                handleClick={() => goToAdjacentPdfPage(-1)}
                disabled={loading || !canGoPdfPagePrev}
                title={t("btnPdfAnnotationPagePrev")}
                className={u3142PdfPageNavBtnClassName}
              />
              <U2000ActionBtn
                variant="secondary"
                icon={FaChevronRight}
                label={<span className="sr-only">{t("btnPdfAnnotationPageNext")}</span>}
                handleClick={() => goToAdjacentPdfPage(1)}
                disabled={loading || !canGoPdfPageNext}
                title={t("btnPdfAnnotationPageNext")}
                className={u3142PdfPageNavBtnClassName}
              />
            </div>
          </div>
        </div>

        <div className="mb-2 flex shrink-0 flex-wrap items-end gap-3 border-b border-gray-200 pb-2">
          <U3140PdfAnnotationShapesMenuButton
            disabled={shapesMenuDisabled}
            menuButtonTitle={shapesMenuButtonTitle}
            activePlacement={activeInsertTool}
            onSelectTool={handleInsertToolFromMenu}
            onCancelPlacement={() => goPlacement({ type: "none" })}
            labels={shapesMenuLabels}
          />
          {textColorToolbarVisible ? (
            <U3140PdfAnnotationTextColorMenuButton
              label={t("lblPdfAnnotationTextStyleMenu")}
              color={textColor}
              disabled={loading}
              onPickColor={handlePickTextColor}
              fontSizeLabel={t("lblPdfAnnotationFontSize")}
              fontSizePts={fontSizePts}
              onFontSizeChange={handleFontSizeInputChange}
              fontSizeMax={120}
              fontSizeDisabled={loading}
              panelFooter={
                <div className="flex flex-wrap items-end gap-4">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600">
                      {t("lblPdfAnnotationAlignGroupHorizontal")}
                    </span>
                    <div
                      className="flex items-center gap-0.5 rounded-sm border border-gray-200 bg-white p-0.5"
                      role="group"
                      aria-label={t("lblPdfAnnotationTextAlign")}
                    >
                      {(
                        [
                          { align: "left" as const, Icon: FaAlignLeft, labelKey: "lblPdfAnnotationAlignLeft" },
                          { align: "center" as const, Icon: FaAlignCenter, labelKey: "lblPdfAnnotationAlignCenter" },
                          { align: "right" as const, Icon: FaAlignRight, labelKey: "lblPdfAnnotationAlignRight" },
                        ] as const
                      ).map(({ align, Icon, labelKey }) => (
                        <button
                          key={align}
                          type="button"
                          disabled={loading}
                          title={t(labelKey)}
                          aria-pressed={textAlign === align}
                          onClick={() => handleTextAlignChange(align)}
                          className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded text-gray-700 hover:bg-gray-100 disabled:opacity-40 ${
                            textAlign === align ? "bg-primary/15 ring-1 ring-primary/40" : ""
                          }`}
                        >
                          <Icon className="h-5 w-5" aria-hidden />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-xs font-medium text-gray-600">
                      {t("lblPdfAnnotationAlignGroupVertical")}
                    </span>
                    <div
                      className="flex items-center gap-0.5 rounded-sm border border-gray-200 bg-white p-0.5"
                      role="group"
                      aria-label={t("lblPdfAnnotationTextValign")}
                      title={t("msgPdfAnnotationTextValignHint")}
                    >
                      {(
                        [
                          { valign: "top" as const, Icon: MdVerticalAlignTop, labelKey: "lblPdfAnnotationValignTop" },
                          { valign: "middle" as const, Icon: MdVerticalAlignCenter, labelKey: "lblPdfAnnotationValignMiddle" },
                          { valign: "bottom" as const, Icon: MdVerticalAlignBottom, labelKey: "lblPdfAnnotationValignBottom" },
                        ] as const
                      ).map(({ valign, Icon, labelKey }) => (
                        <button
                          key={valign}
                          type="button"
                          disabled={loading}
                          title={t(labelKey)}
                          aria-pressed={textBoxVerticalAlign === valign}
                          onClick={() => handleTextBoxVerticalAlignChange(valign)}
                          className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded text-gray-700 hover:bg-gray-100 disabled:opacity-40 ${
                            textBoxVerticalAlign === valign ? "bg-primary/15 ring-1 ring-primary/40" : ""
                          }`}
                        >
                          <Icon className="h-5 w-5" aria-hidden />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              }
            />
          ) : null}
          <U3140PdfAnnotationColorMenuButton
            variant="fill"
            label={t("lblPdfAnnotationFill")}
            noneLabel={t("lblPdfAnnotationFillNone")}
            color={fillColor}
            strokeWidth={strokeWidth}
            disabled={shapesMenuDisabled}
            menuButtonTitle={shapesMenuButtonTitle}
            fillSwatchesDisabled={fillSwatchesDisabled}
            opacityLabel={t("lblPdfAnnotationOpacity")}
            opacity={opacity}
            onOpacityChange={handleOpacitySliderChange}
            opacityDisabled={shapesMenuDisabled}
            onPickColor={handlePickFillColor}
            onClear={handleClearFillColor}
          />
          <U3140PdfAnnotationColorMenuButton
            variant="stroke"
            label={t("lblPdfAnnotationStroke")}
            noneLabel={t("lblPdfAnnotationStrokeNone")}
            color={strokeColor}
            strokeWidth={strokeWidth}
            disabled={strokeToolbarDisabled}
            menuButtonTitle={shapesMenuButtonTitle}
            strokeWidthInputLabel={t("lblPdfAnnotationStrokeWidth")}
            onStrokeWidthInputChange={handleStrokeWidthInputChange}
            strokeWidthInputDisabled={strokeWidthToolbarDisabled}
            onPickColor={handlePickStrokeColor}
            onClear={handleClearStrokeColor}
          />
          <span className="self-center text-sm text-gray-300" aria-hidden>
            |
          </span>
          <U2000ActionBtn
            label={t("btnPdfAnnotationUndo")}
            icon={FaUndo}
            handleClick={() => performUndo()}
            disabled={loading || saving || !canUndo}
            title={t("lblPdfAnnotationUndoHint")}
            variant="secondary"
          />
          <U2000ActionBtn
            label={t("btnPdfAnnotationRedo")}
            icon={FaRedo}
            handleClick={() => performRedo()}
            disabled={loading || saving || !canRedo}
            title={t("lblPdfAnnotationRedoHint")}
            variant="secondary"
          />
          <U2000ActionBtn
            label={t("btnPdfAnnotationDelete")}
            icon={FaTrash}
            handleClick={deleteSelection}
            disabled={loading || saving || !hasFabricSelection}
            variant="secondary"
          />

          <div className="flex-1" />
          <U2000ActionBtn
            variant="secondary"
            label={t("btnPdfAnnotationSaveToolbarPreset")}
            handleClick={() => void handleOpenCreateToolbarPresetDialog()}
            disabled={loading || saving || presetSaving}
            title={t("ttlPdfAnnotationSaveToolbarPreset")}
            hasRole={hasRole(presetEditRole)}
          />
          <U2000ActionBtn
            label={t("dlgLblCancel")}
            handleClick={() => requestClose("cancel")}
            disabled={saving}
            variant="secondary"
          />
          <U2000ActionBtn label={t("btnPdfAnnotationSave")} handleClick={() => void handleSave()} disabled={loading || saving} />
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-0">
          <div className="flex min-h-0 flex-1 flex-row gap-0">
            {toolbarPresets.length > 0 ? (
              <U3140PdfAnnotationToolbarPresetSidebar
                presets={toolbarPresets}
                disabled={loading || saving || presetSaving}
                onApply={handleApplyToolbarPreset}
                getApplyTitle={(p) => t("ttlPdfAnnotationApplyToolbarPreset", {name: p.label })}
                listAriaLabel={t("lblPdfAnnotationToolbarPresetSidebarList")}
                selectedPresetId={appliedToolbarPresetId}
              />
            ) : null}
            <div
              ref={pdfScrollAreaRef}
              className="flex min-h-0 min-w-0 flex-1 justify-center overflow-auto rounded-t-lg rounded-b-none border border-b-0 border-gray-200 bg-gray-100 pt-0 pb-0"
            >
              <div className="relative z-0 inline-block max-w-full">
                <canvas ref={pdfCanvasRef} className="relative z-0 block max-w-none pointer-events-none" />
                <div ref={fabricHostRef} className="absolute left-0 top-0 z-10" />
              </div>
            </div>
          </div>

          <div
            role="status"
            aria-live="polite"
            className="flex min-h-11 shrink-0 flex-row items-stretch justify-between gap-4 border border-t-0 border-gray-200 bg-gray-50 px-3 py-2 text-xs"
          >
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 text-gray-500">
              <span className="block">{t("msgPdfAnnotationKeyboardShortcuts")}</span>
              {annotationSelectionKind !== "none" ? (
                <span className="block text-gray-600">{t("msgPdfAnnotationStyleAppliesToSelection")}</span>
              ) : null}
              {placement.type === "rect" ? (
                <span className="block text-gray-600">{t("msgPdfAnnotationPlaceRect")}</span>
              ) : placement.type === "text" ? (
                <span className="block text-gray-600">{t("msgPdfAnnotationPlaceText")}</span>
              ) : placement.type === "line" || placement.type === "arrow" ? (
                <span className="block text-gray-600">
                  {placement.lineStart ? t("msgPdfAnnotationPlaceLine2") : t("msgPdfAnnotationPlaceLine1")}
                </span>
              ) : null}
            </div>
            <div className="flex max-w-[min(100%,22rem)] shrink-0 flex-col justify-center text-right text-gray-700">
              {hoveredAnnotationMeta ? (
                <span className="block leading-snug">
                  <span className="font-medium text-gray-800">{hoveredAnnotationCreatedBy}</span>
                  <span className="text-gray-500"> · </span>
                  <span className="text-gray-600">{hoveredAnnotationCreatedAt}</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      </U2000BasicDialog>
      <U2000BasicDialog
        isOpen={presetCreateOpen}
        onClose={handleClosePresetDialog}
        onBackdropClick={handleClosePresetDialog}
        title={t("dlgPdfAnnotationSaveToolbarPresetTitle")}
        width="w-[min(95vw,56rem)]"
        height="max-h-[90vh] h-[90vh]"
        overlayClassName={toolbarPresetDialogOverlayClassName}
      >
        <div className="flex h-full min-h-0 flex-1 flex-col">
          <section className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6 py-4">
          {hasExistingPresets ? (
            <div className="flex flex-wrap items-center gap-2">
              {showPresetNewBtn ? (
                <U2000ActionBtn
                  variant="secondary"
                  label={t("btnPdfAnnotationPresetModeCreate")}
                  handleClick={handleNewPresetMode}
                  disabled={presetSaving}
                />
              ) : null}
              {showPresetResetBtn ? (
                <U2000ActionBtn
                  variant="secondary"
                  label={t("btnPdfAnnotationPresetReset")}
                  handleClick={handleResetPresetDialogState}
                  disabled={presetSaving}
                  title={t("ttlPdfAnnotationPresetReset")}
                />
              ) : null}
            </div>
          ) : null}
          {presetDialogMode === "edit" ? (
            <>
              {hasExistingPresets ? (
                <div className="flex min-w-0 max-w-full flex-col gap-1">
                  <U2000ComboBox<U3142PdfToolbarComboItem>
                    formStateHandlers={presetEditComboHandlers}
                    fieldName="presetEdit"
                    label={t("lblPdfAnnotationToolbarPresetToEdit")}
                    data={presetToolbarComboData}
                    keyField="key"
                    descField="label"
                    getValue={(item) => item.key}
                    findItemByValue={(value, data) =>
                      value == null || value === "" ? null : (data.find((d) => String(d.key) === String(value)) ?? null)
                    }
                    formatLabel={(item) => String(item.label)}
                    disabled={presetSaving}
                    placeholder={t("phPdfAnnotationToolbarPresetToEdit")}
                    maxHeight={280}
                    noneItemLabel={t("optPdfAnnotationPresetPickToEdit")}
                  />
                  {presetEditId != null && presetEditId !== "" ? (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        <U2000ActionBtn
                          variant="secondary"
                          label={t("btnPdfAnnotationPresetReloadModel")}
                          handleClick={handleReloadPresetFormSource}
                          disabled={presetSaving}
                          title={t("ttlPdfAnnotationPresetReloadEdit")}
                        />
                        <U2000ActionBtn
                          variant="secondary"
                          label={t("btnPdfAnnotationPresetRename")}
                          handleClick={() => {
                            if (presetEditId == null || presetEditId === "") return;
                            const p = toolbarPresets.find((x) => String(x.id) === String(presetEditId));
                            if (p) {
                              setPresetLabelDraft(p.label);
                            }
                            setPresetRenameOpen(true);
                          }}
                          disabled={presetSaving}
                          title={t("ttlPdfAnnotationPresetRename")}
                        />
                        <U2000ActionBtn
                          variant="secondary"
                          label={t("btnPdfAnnotationPresetDelete")}
                          handleClick={() => setPresetDeleteConfirmOpen(true)}
                          disabled={presetSaving}
                          title={t("ttlPdfAnnotationPresetDelete")}
                        />
                      </div>
                      <div className="space-y-1">
                        <label htmlFor="u3142-toolbar-preset-hint-edit" className="text-sm font-medium text-gray-700">
                          {t("lblPdfAnnotationToolbarPresetHint")}
                        </label>
                        <input
                          id="u3142-toolbar-preset-hint-edit"
                          type="text"
                          className="h-[46px] w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-xs outline-hidden transition hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                          value={presetHintDraft}
                          onChange={(e) => setPresetHintDraft(e.target.value)}
                          placeholder={t("phPdfAnnotationToolbarPresetHint")}
                          disabled={presetSaving}
                          title={t("ttlPdfAnnotationToolbarPresetHint")}
                          maxLength={240}
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <>
              {hasExistingPresets ? (
                <div className="flex min-w-0 max-w-full flex-col gap-1">
                  <U2000ComboBox<U3142PdfToolbarComboItem>
                    formStateHandlers={presetCreateSourceComboHandlers}
                    fieldName="createSource"
                    label={t("lblPdfAnnotationToolbarPresetCreateFrom")}
                    data={presetToolbarComboData}
                    keyField="key"
                    descField="label"
                    getValue={(item) => item.key}
                    findItemByValue={(value, data) =>
                      value == null || value === "" ? null : (data.find((d) => String(d.key) === String(value)) ?? null)
                    }
                    formatLabel={(item) => String(item.label)}
                    disabled={presetSaving}
                    placeholder={t("phPdfAnnotationToolbarPresetCreateFrom")}
                    maxHeight={280}
                    noneItemLabel={t("optPdfAnnotationPresetCreateFromNone")}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <U2000ActionBtn
                      variant="secondary"
                      label={t("btnPdfAnnotationPresetNewFromDefaults")}
                      handleClick={handleCreateModeDefaultValues}
                      disabled={presetSaving}
                      title={t("ttlPdfAnnotationPresetNewFromDefaults")}
                    />
                    {createSourcePresetId != null ? (
                      <U2000ActionBtn
                        variant="secondary"
                        label={t("btnPdfAnnotationPresetReloadModel")}
                        handleClick={handleReloadPresetFormSource}
                        disabled={presetSaving}
                        title={t("ttlPdfAnnotationPresetReloadModel")}
                      />
                    ) : null}
                  </div>
                  {isPresetFormVisible ? (
                    <div className="space-y-1">
                      <label htmlFor="u3142-toolbar-preset-hint-create" className="text-sm font-medium text-gray-700">
                        {t("lblPdfAnnotationToolbarPresetHint")}
                      </label>
                      <input
                        id="u3142-toolbar-preset-hint-create"
                        type="text"
                        className="h-[46px] w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-xs outline-hidden transition hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                        value={presetHintDraft}
                        onChange={(e) => setPresetHintDraft(e.target.value)}
                        placeholder={t("phPdfAnnotationToolbarPresetHint")}
                        disabled={presetSaving}
                        title={t("ttlPdfAnnotationToolbarPresetHint")}
                        maxLength={240}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
          {presetDialogMode !== "edit" || presetRenameOpen ? (
            <div className="space-y-1">
              <label htmlFor="u3142-toolbar-preset-label" className="text-sm font-medium text-gray-700">
                {t("lblPdfAnnotationToolbarPresetLabel")}
              </label>
              <input
                id="u3142-toolbar-preset-label"
                type="text"
                className="h-[46px] w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-xs outline-hidden transition hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder={t("phPdfAnnotationToolbarPresetLabel")}
                value={presetLabelDraft}
                onChange={(e) => setPresetLabelDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && isPresetFormVisible) {
                    e.preventDefault();
                    void handleSubmitToolbarPresetDialog();
                  }
                }}
                disabled={presetSaving}
                maxLength={80}
              />
              {!hasExistingPresets && isPresetFormVisible ? (
                <div className="space-y-1 pt-1">
                  <label htmlFor="u3142-toolbar-preset-hint-first" className="text-sm font-medium text-gray-700">
                    {t("lblPdfAnnotationToolbarPresetHint")}
                  </label>
                  <input
                    id="u3142-toolbar-preset-hint-first"
                    type="text"
                    className="h-[46px] w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-xs outline-hidden transition hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                    value={presetHintDraft}
                    onChange={(e) => setPresetHintDraft(e.target.value)}
                    placeholder={t("phPdfAnnotationToolbarPresetHint")}
                    disabled={presetSaving}
                    title={t("ttlPdfAnnotationToolbarPresetHint")}
                    maxLength={240}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {isPresetFormVisible ? (
            <U3140PdfAnnotationToolbarPresetStyleForm
              value={presetStylesDraft}
              onChange={setPresetStylesDraft}
              disabled={presetSaving}
              i18nNamespace={i18nNamespace}
            />
          ) : null}
          </section>
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4 rounded-b-2xl">
            <U2000ActionBtn
              label={
                presetDialogMode === "edit"
                  ? t("btnPdfAnnotationSaveToolbarPresetUpdate")
                  : t("btnPdfAnnotationCreatePreset")
              }
              handleClick={() => void handleSubmitToolbarPresetDialog()}
              disabled={
                presetSaving ||
                !isPresetFormVisible ||
                !presetLabelDraft.trim() ||
                (presetDialogMode === "edit" && (presetEditId == null || presetEditId === ""))
              }
            />
            <U2000ActionBtn
              label={t("dlgLblCancel")}
              variant="secondary"
              handleClick={handleCancelPresetDialog}
              disabled={presetSaving}
            />
            <U2000ActionBtn
              label={t("btnPdfAnnotationPresetClose")}
              variant="secondary"
              handleClick={handleClosePresetDialog}
              disabled={presetSaving}
            />
          </footer>
        </div>
      </U2000BasicDialog>
      <U2000YesNoDialog
        isOpen={presetDeleteConfirmOpen}
        overlayClassName={presetDeleteConfirmOverlayClassName}
        onClose={(r) => {
          setPresetDeleteConfirmOpen(false);
          if (r === "yes") {
            void handleDeleteSelectedPreset();
          }
        }}
        title={t("dlgPdfAnnotationPresetDeleteTitle")}
        width="max-w-md"
      >
        <p className="text-sm text-gray-700">{t("msgPdfAnnotationPresetDeleteConfirm")}</p>
      </U2000YesNoDialog>
      <U2000YesNoDialog
        isOpen={discardConfirmOpen}
        overlayClassName={discardConfirmOverlayClassName}
        onClose={(r) => {
          setDiscardConfirmOpen(false);
          if (r === "yes") {
            performCloseInternal();
          }
        }}
        title={t("dlgPdfAnnotationDiscardTitle")}
        width="max-w-md"
      >
        <p className="text-sm text-gray-700">{t("msgPdfAnnotationDiscardChanges")}</p>
      </U2000YesNoDialog>
    </>
  );
};
